import * as THREE from 'three';
import { EventBus } from '@/core/EventBus';
import { Quat } from '@/core/math/Quat';
import { Vec3 } from '@/core/math/Vec3';
import { DEG, clamp, clamp01, formatSpeed, smoothstep, wrapTau } from '@/core/math/scalar';
import { FixedStepAccumulator } from '@/physics/Integrators';
import { AtmosphereModel } from '@/physics/Atmosphere';
import { GravitySystem } from '@/physics/GravitySystem';
import { FloatingOrigin } from '@/frames/FloatingOrigin';
import { latLonFromPosition, tangentBasis } from '@/frames/PlanetaryCoordinates';
import { BIOMES } from '@/planet/BiomeDef';
import { DEFAULT_TERRAIN_QUALITY, PlanetSurface, VegetationQualitySettings } from '@/planet/PlanetSurface';
import { BiomeBands, classifyBiome, climateAt, humidityAt } from '@/planet/TerrainPipeline';
import { WorkerPool } from '@/planet/WorkerPool';
import { AstronomicalLayer, discAlbedo } from '@/render/AstronomicalLayer';
import { SunGlare, sunBloomFactor } from '@/render/SunGlare';
import { detectCapabilities, recommendedWorkerCount } from '@/render/Capabilities';
import { LayeredRenderer } from '@/render/LayeredRenderer';
import { CloudLayer } from '@/render/CloudLayer';
import { GraphicsSettings, loadGraphicsSettings, saveGraphicsSettings } from '@/render/GraphicsSettings';
import { DevSessionFreeFlight, loadDevSessionState, patchDevSessionState } from '@/game/DevSessionState';
import { OceanRenderer } from '@/render/OceanRenderer';
import { QUALITY_PROFILES, QualityLevel, QualitySettings, selectQualityProfile } from '@/render/QualityProfiles';
import { ShipModel } from '@/render/ShipModel';
import { CharacterModel } from '@/render/CharacterModel';
import { getVegetationShadowUniforms } from '@/render/vegetation/VegetationGeometry';
import { setShadowCascadeUniforms, setShadowFadeUniform } from '@/render/shaders/shadowCascade';
import { Starfield } from '@/render/Starfield';
import { TerrainRenderer } from '@/render/TerrainRenderer';
import { SURFACE_DAYLIGHT_END, SURFACE_DAYLIGHT_START } from '@/render/shaders/TerrainMaterial';
import { VegetationLayer, vegetationScatterDistanceFor } from '@/render/vegetation/VegetationLayer';
import { TargetArrow } from '@/render/TargetArrow';
import { TrajectoryLine } from '@/render/TrajectoryLine';
import { RouteOverlay } from '@/render/RouteOverlay';
import { WorldMarkerLayer } from '@/render/WorldMarkers';
import { FlightComputer } from '@/spacecraft/FlightComputer';
import { ManeuverPlanner } from '@/spacecraft/ManeuverPlanner';
import { MacroRunner, MacroId } from '@/spacecraft/macros/MacroRunner';
import { MacroContext } from '@/spacecraft/macros/Macro';
import { FlightEnvironment, Spacecraft } from '@/spacecraft/Spacecraft';
import { ChainedPrediction, TrajectoryEvent, TrajectoryPredictor } from '@/spacecraft/TrajectoryPredictor';
import { TransferToTargetMacro } from '@/spacecraft/macros/TransferToTarget';
import { findShipDef, STARTER_SHIP_ID } from '@/spacecraft/data/ships';
import { TimeWarpController, WarpContext } from '@/timewarp/TimeWarpController';
import { CelestialBody } from '@/universe/CelestialBody';
import { SolarSystem } from '@/universe/SolarSystem';
import { isStarDef, TerrainProfile } from '@/universe/BodyDef';
import {
  apoapsisRadius,
  circularVelocity,
  elementsFromState,
  escapeVelocity,
  orbitalPeriod,
  periapsisRadius,
  timeToApoapsis,
  timeToPeriapsis,
} from '@/universe/OrbitalElements';
import { findLevelSite } from '@/planet/LandingSite';
import { CharacterController } from '@/entities/CharacterController';
import { createRepository } from '@/persistence/IndexedDbRepository';
import {
  ChunkDelta,
  SaveRepository,
  ShipRecord,
  WorldRepository,
} from '@/persistence/WorldRepository';
import { AudioSystem } from '@/audio/AudioSystem';
import { FreeFlightCamera } from './FreeFlight';
import { InputManager } from './InputManager';
import { applySave, buildSave, SaveContext } from './SaveSystem';
import {
  BodyFrameDirection,
  DiagnosticsSnapshot,
  EnvironmentSnapshot,
  FreeFlightSnapshot,
  GameSnapshot,
  NavigationSnapshot,
  NotificationSnapshot,
  PerformanceSnapshot,
  PlayerSnapshot,
  VesselSnapshot,
  WarpSnapshot,
} from './Snapshot';
import { TourConfig } from '@/tour/TourConfig';
import { TourDirector } from '@/tour/TourDirector';

export type CameraMode = 'cockpit' | 'chase' | 'onFoot';

export interface GameEvents extends Record<string, unknown> {
  snapshot: GameSnapshot;
  notification: NotificationSnapshot;
  ready: { qualityLevel: QualityLevel };
}

const PHYSICS_STEP = 1 / 60;
/** Simulation seconds between recomputes of the idle coast-route preview. */
const IDLE_ROUTE_INTERVAL = 5;

/**
 * Converts a "+Z is forward" orientation into a camera orientation. Three.js
 * cameras look along their own -Z, so a half turn about the up axis is needed.
 */
const VIEW_FLIP = Quat.fromAxisAngle(new Vec3(0, 1, 0), Math.PI);
const SNAPSHOT_INTERVAL = 0.1;
/** Save slot the quick-save key and the automatic restore both use. */
const AUTOSAVE_ID = 'autosave';

/**
 * Where a new session starts: a named landing site on the home world, chosen
 * for a coastline within walking distance and a horizon worth looking at.
 */
const START_SITE = { name: 'Meridian Reach', latitude: 14.2 * DEG, longitude: -38.6 * DEG };

/**
 * Scale from physical planetshine irradiance to something the display can show.
 *
 * Derived rather than picked: a planet-lit night on Ferrum should read around
 * 0.20 on screen, which inverts through the sRGB transfer and the ACES toe to a
 * shader input near 0.089, and at the night exposure that needs a planetshine
 * intensity near 0.24 against a physical 0.0019. See the use site for why the
 * gap cannot be closed by exposure alone.
 */
const PLANETSHINE_DISPLAY_GAIN = 125;

/**
 * How close (in body radii) the camera must be to a body — beyond the view
 * body itself, which is always active — before it also gets real streamed
 * terrain instead of the astronomical layer's compressed impostor, and how
 * far it must retreat before losing it again. The gap between the two is
 * hysteresis, the same reason `PlanetSurface`'s own split/merge thresholds
 * are not equal: without it, a camera orbiting near the boundary would
 * promote and demote the body every frame, each flip tearing down and
 * rebuilding a chunk tree.
 *
 * Only ever applied within a body's own local group (see `terrainGroupOf`) —
 * its parent and siblings. A planet and its moons can be close enough to
 * matter at once, which a single exclusive "active body" cannot represent;
 * every other body in the system still hands off to the astronomical layer on
 * its own, unrelated to this.
 */
/**
 * How long a body takes to cross-fade between its distant impostor and its
 * real streamed terrain, seconds.
 *
 * Long enough that the eye reads it as a dissolve rather than a cut, short
 * enough that the dither grain never becomes something to look at. The fade is
 * driven in real time, not simulation time, so it behaves the same under time
 * warp as it does at 1x.
 */
const SURFACE_FADE_SECONDS = 0.6;

const PROMOTE_RADII = 12;
const DEMOTE_RADII = 18;

/** Independently switchable render layers. See Game.debugLayers. */
export type DebugLayer =
  | 'surface'
  | 'atmosphere'
  | 'sky'
  | 'clouds'
  | 'ocean'
  | 'vegetation'
  | 'stars'
  | 'galaxy'
  | 'sunGlare'
  | 'distantBodies'
  | 'vessel'
  | 'trajectory'
  | 'targetMarker';

/**
 * How one celestial body is lit as seen from one viewpoint. Built by
 * `Game.bodyLighting`, which is the single place these terms are derived.
 */
interface BodyLighting {
  /** Unit vector toward the star, in the body's inertial axes. */
  readonly sunDirection: Vec3;
  /** True stellar irradiance at the viewpoint, W/m^2, unclamped. */
  readonly irradiance: number;
  /** Irradiance normalised and clamped for the shaders. See `bodyLighting`. */
  readonly sunIntensity: number;
  /** Viewpoint altitude above this body's surface, metres. */
  readonly altitude: number;
  /** Atmospheric density at that altitude, kg/m3. 0 for an airless body. */
  readonly density: number;
  /** How much skylight the air is delivering here, 0..1. */
  readonly skyStrength: number;
  /** Camera-local terminator factor, 0 night to 1 day. */
  readonly daylight: number;
  /** Physical planetshine irradiance, same units as `irradiance`. */
  readonly planetshine: number;
  /** Planetshine scaled into the range the display can show. See `render`. */
  readonly planetshineForDisplay: number;
}

/**
 * The game.
 *
 * Responsibilities, in order of the frame:
 *
 *   1. Read input.
 *   2. Decide how much simulated time to advance (time warp).
 *   3. Advance the simulation, either by full physics sub-steps or by
 *      analytical propagation.
 *   4. Update terrain streaming and the floating origin.
 *   5. Build the render state and draw.
 *   6. Publish a snapshot for the UI, at a fixed low rate.
 *
 * Nothing below this class knows about React, the DOM, or the renderer, and
 * this class never reaches into the UI. The only channel is the snapshot.
 */
export class Game {
  readonly events = new EventBus<GameEvents>();
  readonly system: SolarSystem;
  readonly gravity: GravitySystem;
  readonly ship: Spacecraft;
  readonly computer = new FlightComputer();
  readonly predictor: TrajectoryPredictor;
  readonly planner: ManeuverPlanner;
  readonly macros = new MacroRunner();
  readonly warp = new TimeWarpController();
  readonly input: InputManager;
  readonly renderer: LayeredRenderer;
  readonly character: CharacterController;
  readonly freeFlight = new FreeFlightCamera();
  readonly audio = new AudioSystem();

  quality: QualitySettings;
  /** User-facing resolution multiplier; see `setRenderScale`. */
  renderScale = 1;
  terrainSelfShadowEnabled = true;
  eclipseShadowsEnabled = true;
  vegetationEnabled = true;

  /** Simulation time, seconds. */
  time = 0;
  paused = false;
  cameraMode: CameraMode = 'cockpit';
  onFoot = false;

  // Physics warp runs up to 10x, which is ten steps in a single 60 Hz frame,
  // so the cap has to leave room for that plus a little catch-up. Below it the
  // backlog is deliberately dropped rather than chased.
  private readonly accumulator = new FixedStepAccumulator(PHYSICS_STEP, 16);
  private readonly floatingOrigin: FloatingOrigin;
  private readonly pool: WorkerPool;
  private readonly astronomical: AstronomicalLayer;
  private readonly sunGlare = new SunGlare();
  private readonly starfield: Starfield;
  private readonly shipModel: ShipModel;
  private readonly characterModel: CharacterModel;
  private readonly trajectoryLine = new TrajectoryLine();
  private readonly apsisMarkers = new WorldMarkerLayer(9);
  private readonly targetMarker = new WorldMarkerLayer(11);
  private readonly targetArrow = new TargetArrow(0xffd166, 1.1);
  /** Distance the off-screen target arrow sits in front of the camera, render units. */
  private readonly targetArrowDistance = 30;

  /** Multi-arc planned-route preview. See `RouteOverlay`. */
  private readonly routeOverlay: RouteOverlay;
  private idleRouteCache: ChainedPrediction | null = null;
  private idleRouteTime = -Infinity;

  private surfaces = new Map<string, PlanetSurface>();
  private terrainRenderers = new Map<string, TerrainRenderer>();
  private oceans = new Map<string, OceanRenderer>();
  private clouds = new Map<string, CloudLayer>();
  private vegetation = new Map<string, VegetationLayer>();
  private atmospheres = new Map<string, AtmosphereModel>();
  private biomeBands = new Map<string, BiomeBands>();

  private activeSurface: PlanetSurface | null = null;
  private activeTerrain: TerrainRenderer | null = null;
  private activeOcean: OceanRenderer | null = null;
  private activeClouds: CloudLayer | null = null;
  private activeVegetation: VegetationLayer | null = null;
  /**
   * Body the `active*` pointers above were last aimed at. Compared against
   * `viewBody` once a frame by `syncActiveBody`, which is what stops them
   * going stale when a travel path forgets to re-latch them.
   */
  private activeViewBodyId: string | null = null;

  /**
   * Bodies currently promoted to real streamed terrain: the view body plus
   * any of its parent/siblings within `PROMOTE_RADII`. Recomputed every frame
   * by `updateActiveBodySet`; `activeSurface`/`activeTerrain`/`activeOcean`/
   * `activeClouds` above stay pointed at the view body specifically, since
   * that is the one whose altitude and atmosphere the character, camera and
   * lighting all actually depend on.
   */
  private activeBodyIds = new Set<string>();
  /**
   * Subset of `activeBodyIds` whose astronomical impostor is hidden because
   * real terrain has, at some point since being promoted, fully covered it
   * (`PlanetSurface.fullyStreamed`). Checked once per body per promotion and
   * then left alone — see `applyActiveBodySet` — rather than re-derived every
   * frame, which would flicker the impostor back on every time ordinary
   * streaming has a chunk in flight.
   */
  private readyBodyIds = new Set<string>();

  /**
   * Per-body cross-fade against the distant impostor: 1 is fully real terrain,
   * 0 is fully impostor. See `updateSurfaceFades`, and GLSL_DITHER for how the
   * opaque surfaces actually blend.
   */
  private surfaceFades = new Map<string, number>();

  /**
   * Cloud-only bodies the promotion test actually wants right now, as distinct
   * from `activeCloudOnlyIds`, which also holds the ones still dissolving back
   * out. The fade's target has to come from this set: reading the retained one
   * would tell a retiring body it still wants to be here and it would never
   * leave.
   */
  private desiredCloudOnlyIds = new Set<string>();

  /**
   * Bodies with no terrain of their own but with a real cloud deck — gas
   * giants — currently shown as that true-scale deck instead of the
   * astronomical impostor. A gas giant can never enter `activeBodyIds`
   * (`ensureSurface` requires `def.terrain`, and a gas giant has none), so
   * without this it stays the crude impostor sphere — flat continent-like
   * fbm meant for rocky worlds, no bands, no depth — at every distance,
   * never handing off to something that actually looks like its close-up
   * self the way every terrain-having body does. Unlike terrain there is no
   * async streaming to wait for (the deck is a fully procedural shell, not
   * chunks), so a body can enter and leave this set the same frame the
   * distance check flips, with no ready-latch needed.
   */
  private activeCloudOnlyIds = new Set<string>();

  private notifications: NotificationSnapshot[] = [];
  private notificationId = 1;
  private snapshotTimer = 0;
  private frame = 0;
  private lastRealTime = 0;
  private fpsAccumulator = 0;
  private fpsFrames = 0;
  private fps = 60;
  private physicsMs = 0;
  private rafHandle = 0;
  private disposed = false;

  private visitedBodies = new Set<string>();
  private targetBodyId: string | null = null;
  private lastSnapshot: GameSnapshot | null = null;

  private saves: SaveRepository | null = null;
  private worldStore: WorldRepository | null = null;
  private saveInFlight = false;

  private readonly scratchA = new Vec3();
  private readonly scratchB = new Vec3();
  private readonly scratchC = new Vec3();
  private readonly viewOrientation = Quat.identity();
  // Reused per frame: planetshine is recomputed every frame and allocating a
  // Vec3 and a Color each time would be per-frame garbage for no reason.
  private readonly planetshineDirection = new Vec3(0, 1, 0);
  private readonly planetshineColor = new THREE.Color(1, 1, 1);
  // Same reuse, for whichever non-view bodies `updateBodyVisuals` handles.
  private readonly secondaryPlanetshineDirection = new Vec3(0, 1, 0);
  private readonly secondaryPlanetshineColor = new THREE.Color(1, 1, 1);
  /** The two pairs above, in the shape `bodyLighting` takes. */
  private readonly primaryShine = {
    direction: this.planetshineDirection,
    color: this.planetshineColor,
  };
  private readonly secondaryShine = {
    direction: this.secondaryPlanetshineDirection,
    color: this.secondaryPlanetshineColor,
  };
  // Terrain ambient is rebuilt from the body's haze every frame, for every
  // promoted body. These were two `new THREE.Color` per body per frame.
  private readonly scratchSkyAmbient = new THREE.Color();
  private readonly scratchGroundAmbient = new THREE.Color();
  /** Star tint handed to every lit layer. Constant, but Three wants a Color. */
  private readonly starColor = new THREE.Color(1, 0.955, 0.9);
  /** Reused for the directional light's per-frame direction. */
  private readonly scratchStarLightDirection = new THREE.Vector3();

  /**
   * The body whose inertial frame holds `freeFlight.position`, and whose world
   * the renderer draws while the camera is detached.
   *
   * Null whenever free flight is off, in which case the vessel's reference body
   * serves both roles. See `viewBody` for why they have to be separable.
   */
  private freeFlightBody: CelestialBody | null = null;

  /**
   * Which of the view body's frames the free camera's state is expressed in.
   *
   * There are genuinely two kinds of navigation here and they want different
   * frames. Out in space the inertial frame is right: the world turns beneath
   * you and that is what it is doing. Down near a surface it is wrong, because
   * a camera holding station in the inertial frame is not holding station over
   * the ground — Halcyon's equator moves at 470 m/s, so the terrain slides out
   * from under a "stationary" camera at a brisk walking pace times a hundred.
   *
   * In `fixed` the camera co-rotates with the body, so a hover stays over the
   * same rock and forward follows the surface.
   */
  private freeFlightFrame: 'inertial' | 'fixed' = 'inertial';

  /**
   * Seconds of continuous free-flight input silence before the idle position
   * is autosaved to `DevSessionState`. Debounced rather than saved every
   * frame so parking the camera to stare at something doesn't spam
   * localStorage on every tick of stillness.
   */
  private static readonly FREE_FLIGHT_AUTOSAVE_DEBOUNCE = 1;

  /**
   * Clearance kept between the free-flight camera and the terrain surface it
   * is clamped to, metres. Without it the camera sits exactly on the ground
   * it was just stopped from passing through, which renders as the near
   * plane clipping into terrain the instant the pilot noses down.
   */
  private static readonly FREE_FLIGHT_GROUND_MARGIN = 1;

  /** True once movement has happened since the last autosave (or toggle). */
  private freeFlightAutosavePending = false;
  /** Seconds of input silence accumulated toward the debounce above. */
  private freeFlightIdleFor = 0;

  /** The scripted camera tour. See the "Tour mode" section below. */
  private readonly tour: TourDirector;

  constructor(private readonly canvas: HTMLCanvasElement, qualityOverride?: QualityLevel) {
    // Named so the TourHost getters below can reach live fields; an arrow
    // function would capture `this` fine, but a getter defined in an object
    // literal gets the literal as its own `this`.
    const self = this;
    const caps = detectCapabilities();
    // `?quality=` still wins over a saved preference for the quality level
    // specifically: automated visual checks rely on it to see past what
    // capability detection can (a software renderer), and a stale saved
    // preference should not defeat that override.
    const saved = loadGraphicsSettings();
    this.quality =
      qualityOverride && qualityOverride !== 'custom'
        ? { ...QUALITY_PROFILES[qualityOverride] }
        : saved.qualityLevel
          ? { ...QUALITY_PROFILES[saved.qualityLevel] }
          : selectQualityProfile(caps);
    this.renderScale = saved.renderScale;
    this.terrainSelfShadowEnabled = saved.terrainSelfShadow;
    this.eclipseShadowsEnabled = saved.eclipseShadows;
    this.vegetationEnabled = saved.vegetationEnabled;

    // Automated visual checks need the drawing buffer preserved so a
    // screenshot taken between frames is not an undefined buffer.
    if (typeof location !== 'undefined' && location.search.includes('capture')) {
      this.quality = { ...this.quality, captureMode: true };
    }

    this.renderer = new LayeredRenderer(canvas, this.quality, caps);
    // Terrain geometry keeps no CPU copy to re-upload from, so a context loss
    // has to be answered by regenerating it. `invalidateAll` drops every
    // resident chunk's data and lets the ordinary streaming path rebuild it,
    // including the "a parent stays visible until all four children are ready"
    // rule, so no hole appears while it does.
    this.renderer.onContextRestored = () => {
      for (const surface of this.surfaces.values()) surface.invalidateAll();
    };
    this.renderer.setRenderScale(this.renderScale);
    this.renderer.setTerrainShadowEnabled(this.terrainSelfShadowEnabled);
    this.input = new InputManager(canvas);

    this.system = new SolarSystem();
    this.gravity = new GravitySystem(this.system);
    this.predictor = new TrajectoryPredictor(this.system);
    this.planner = new ManeuverPlanner(this.predictor);

    for (const b of BIOMES) {
      this.biomeBands.set(b.id, {
        temperature: b.temperature,
        humidity: b.humidity,
        elevation: b.elevation,
        maxSlope: b.maxSlope,
      });
    }

    this.pool = new WorkerPool(recommendedWorkerCount(caps));

    // --- Vessel -------------------------------------------------------------
    const home = this.system.homePlanet;
    const shipDef = findShipDef(STARTER_SHIP_ID);
    this.ship = new Spacecraft(shipDef, home);

    this.shipModel = new ShipModel(shipDef);
    this.renderer.worldScene.add(this.shipModel.root);
    this.characterModel = new CharacterModel();
    this.renderer.worldScene.add(this.characterModel.root);
    this.renderer.worldScene.add(this.trajectoryLine.line);
    this.renderer.worldScene.add(this.apsisMarkers.points);
    this.routeOverlay = new RouteOverlay(this.system.frames, this.system.root, this.renderer.worldScene);
    this.renderer.worldScene.add(this.targetMarker.points);
    this.renderer.worldScene.add(this.targetArrow.sprite);

    // --- Background ---------------------------------------------------------
    this.starfield = new Starfield(this.quality.starCount, this.system.seed);
    this.renderer.deepSpaceScene.add(this.starfield.object);
    this.astronomical = new AstronomicalLayer(this.system);
    this.renderer.astronomicalScene.add(this.astronomical.root);
    // True-scale near impostors (see AstronomicalLayer.update's `nearGroupIds`)
    // live in the world scene so they depth-test correctly against a promoted
    // sibling's real terrain instead of always losing to it.
    this.renderer.worldScene.add(this.astronomical.nearRoot);
    this.renderer.overlayScene.add(this.sunGlare.root);

    // --- Frames -------------------------------------------------------------
    this.floatingOrigin = new FloatingOrigin(this.system.frames, home.inertialFrameId, 4000);

    // --- Surface and character ---------------------------------------------
    const surface = this.ensureSurface(home);
    this.character = new CharacterController(surface.heightField);

    // Place the vessel on the surface at the starting site.
    this.placeShipAtSurface(home, START_SITE.latitude, START_SITE.longitude);
    this.visitedBodies.add(home.id);

    // --- Tour ---------------------------------------------------------------
    // The adapter is written out rather than passing `this`, because it is the
    // whole of what the tour is allowed to reach for — and because
    // `freeFlightBody`/`freeFlightFrame` stay private here while still being
    // the two things the director genuinely has to reassign. See TourHost.
    this.tour = new TourDirector({
      system: this.system,
      surfaces: this.surfaces,
      freeFlight: this.freeFlight,
      get time(): number {
        return self.time;
      },
      get freeFlightBody(): CelestialBody | null {
        return self.freeFlightBody;
      },
      set freeFlightBody(body: CelestialBody | null) {
        self.freeFlightBody = body;
      },
      get freeFlightFrame(): 'inertial' | 'fixed' {
        return self.freeFlightFrame;
      },
      set freeFlightFrame(frame: 'inertial' | 'fixed') {
        self.freeFlightFrame = frame;
      },
      get shipReferenceBody(): CelestialBody {
        return self.ship.referenceBody;
      },
      cameraPositionInFrame: () => this.cameraPositionInFrame(),
      sunDirectionAt: (b, p, t) => this.sunDirectionAt(b, p, t),
      setActiveBody: (b) => this.setActiveBody(b),
      enableFreeFlight: () => {
        if (!this.freeFlight.active) this.toggleFreeFlight();
      },
      notify: (severity, title, message) => this.notify(severity, title, message),
    });

    this.notify('info', 'Systems nominal', `${shipDef.name} standing by at ${START_SITE.name}.`);
    this.events.emit('ready', { qualityLevel: this.quality.level });

    // Free flight moves every frame without ever calling a setter (see
    // FreeFlightCamera.update), so the only reliable moment to capture
    // "wherever the camera ended up" is right before the page actually goes
    // away — which a routine shader-edit reload triggers same as a real
    // close. toggleFreeFlight already covers the on/off transitions
    // themselves; this covers drifting around in between them.
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.persistDevSessionFreeFlight());
    }
  }

  // =========================================================================
  // Setup helpers
  // =========================================================================

  private ensureSurface(body: CelestialBody): PlanetSurface {
    const existing = this.surfaces.get(body.id);
    if (existing) return existing;
    if (!body.def.terrain) throw new Error(`Body ${body.id} has no terrain`);

    const surface = new PlanetSurface(
      body,
      this.system.seed,
      this.pool,
      {
        onChunkReady: (record) => {
          this.terrainRenderers.get(body.id)?.addChunk(record);
          this.vegetation.get(body.id)?.onChunkReady(record);
        },
        onChunkRemoved: (key) => {
          this.terrainRenderers.get(body.id)?.removeChunk(key);
          this.vegetation.get(body.id)?.onChunkRemoved(key);
        },
      },
      { ...DEFAULT_TERRAIN_QUALITY, ...this.quality.terrain },
      this.vegetationQuality(),
    );
    this.surfaces.set(body.id, surface);

    const terrain = new TerrainRenderer(
      body,
      surface.seaLevelRadius,
      this.quality.atmosphereSteps,
      this.quality.atmosphereLightSteps,
    );
    this.terrainRenderers.set(body.id, terrain);

    if (body.atmosphere.cloudCoverage > 0 && this.quality.cloudQuality > 0) {
      this.clouds.set(
        body.id,
        new CloudLayer(body, this.quality.cloudQuality, surface.seaLevelRadius),
      );
    }

    if (surface.hasOcean && this.quality.oceanQuality > 0) {
      const ocean = new OceanRenderer(body, surface.seaLevelRadius, this.quality.oceanQuality);
      ocean.setSsrReflection(this.renderer.screenSpaceReflection);
      // The SSR capture must never see the ocean's own surface: a reflected
      // ray would otherwise re-hit the water and regress forever.
      this.renderer.addSsrExclude(ocean.root);
      this.oceans.set(body.id, ocean);
    }

    // Skipped entirely on a body with no vegetated/aquatic biome permitted —
    // no group, no materials, nothing to update every frame for a barren or
    // volcanic world.
    if (surface.hasVegetation) {
      // Clamped against what this body's LOD tree actually generates rather
      // than taken straight from the quality tier — see
      // `vegetationScatterDistanceFor`. The bound is per-body because it
      // scales with the body's radius.
      const vegetationLayer = new VegetationLayer(
        vegetationScatterDistanceFor(this.quality.scatterDistance, surface.vegetationRange),
      );
      // The SSR capture is meant to hold the opaque ground/cliffs the ocean
      // reflects, not the scatter of trees and rocks sitting on top of it —
      // included, they double the tree line into the water as a reflected
      // copy and the billboard tier's camera-facing quads (which don't turn
      // to face the SSR capture camera) draw as flat, wrongly-angled cards.
      this.renderer.addSsrExclude(vegetationLayer.root);
      this.vegetation.set(body.id, vegetationLayer);
    }

    this.atmospheres.set(body.id, new AtmosphereModel(body));
    return surface;
  }

  /**
   * The gas-giant counterpart to `ensureSurface`: a real cloud deck with no
   * `PlanetSurface`/`TerrainRenderer` behind it, for a body whose visible
   * "surface" IS its cloud deck. `CloudLayer` only ever needed a body,
   * quality and an optional waterline radius — never the heightfield —so
   * this needs none of `ensureSurface`'s other machinery.
   */
  private ensureCloudDeck(body: CelestialBody): void {
    if (this.clouds.has(body.id)) return;
    if (body.atmosphere.cloudCoverage <= 0 || this.quality.cloudQuality <= 0) return;
    this.clouds.set(body.id, new CloudLayer(body, this.quality.cloudQuality, 0));
  }

  /**
   * The live terrain profile backing `bodyId`'s generated chunks, for the
   * debug UI's live-tuning panel. `undefined` before the body's surface has
   * been constructed (it has no terrain to tune yet). The returned object is
   * the actual profile in use — read it for display, but go through
   * `updateTerrainProfile` to change it, since a field written directly here
   * would desync `seaLevel` and the continental calibration from it.
   */
  getTerrainProfile(bodyId: string): Readonly<TerrainProfile> | undefined {
    return this.surfaces.get(bodyId)?.params.profile;
  }

  /**
   * Live-tunes `bodyId`'s terrain profile and regenerates every chunk from
   * it. A no-op if the body's surface has not been constructed yet, which
   * matches `getTerrainProfile` returning `undefined` in that case.
   */
  updateTerrainProfile(bodyId: string, partial: Partial<TerrainProfile>): void {
    this.surfaces.get(bodyId)?.updateProfile(partial);
  }

  /** Positions the vessel on the ground at a latitude and longitude. */
  private placeShipAtSurface(body: CelestialBody, latitude: number, longitude: number): void {
    const surface = this.ensureSurface(body);
    const cosLat = Math.cos(latitude);
    const nominal = new Vec3(cosLat * Math.cos(longitude), Math.sin(latitude), -cosLat * Math.sin(longitude));

    // Set down on ground the gear can actually cope with, rather than on
    // whatever happens to be under the nominal coordinate.
    const site = findLevelSite(surface.heightField, nominal, this.ship.def.landingGear.contacts, {
      // A spaceport is on land. If the authored coordinate lands in water on
      // this seed, move to the nearest dry, level ground.
      requireLand: true,
      tolerance: 1.6,
    });
    const dirFixed = site.direction;
    const posFixed = dirFixed.clone().mulScalar(site.spawnRadius);

    const state = this.system.frames.transformState(
      posFixed,
      new Vec3(0, 0, 0),
      body.fixedFrameId,
      body.inertialFrameId,
      this.time,
    );

    // Nose north, dorsal away from the planet.
    const basis = tangentBasis(state.position);
    const orientation = Quat.lookRotation(basis.north, basis.up);

    this.ship.rebaseTo(body, state.position, state.velocity, orientation, new Vec3(0, 0, 0));
    this.floatingOrigin.reset(body.inertialFrameId, state.position.clone());
    this.setActiveBody(body);
  }

  /**
   * Points the view-body-specific state — character collision, and the
   * altitude/atmosphere-aware lighting `render` computes for whoever the
   * camera is actually standing on — at `body`.
   *
   * This no longer touches the scene graph or the astronomical layer: which
   * bodies actually get real terrain in the world (this one and possibly its
   * parent/siblings) is decided fresh every frame by `updateActiveBodySet`,
   * independent of whichever caller last moved the view body.
   */
  private setActiveBody(body: CelestialBody): void {
    this.activeViewBodyId = body.id;

    // A body with nothing to stand on — a gas giant. Every pointer below is
    // per-surface state, and there is no surface, so they must be CLEARED
    // rather than left where they were.
    //
    // They used to be left, because the call sites all read
    // `if (body.def.terrain) this.setActiveBody(body)` and a gas giant simply
    // skipped the call. `viewBody` still followed the camera, so the game
    // ended up drawing the world it had just left with the coordinates of the
    // world it was now at: `render`'s primary block is unconditional on
    // `this.activeTerrain` and never checked that the two agreed. Jumping to
    // Aegis from Halcyon reproducibly left `activeTerrain = halcyon` with
    // Halcyon's terrain root still attached to the world scene, being fed
    // Aegis-relative camera positions — the same class of bug as the
    // `restoreShip` one in NEXT_SESSION.md, whose symptom was a body's
    // geometry drawn at an arbitrary, unrelated place and scale.
    //
    // The cloud deck is deliberately not picked up here either: a promoted
    // gas giant's deck is positioned and lit by `render`'s
    // `activeCloudOnlyIds` loop, which does not skip the view body, and
    // `applyCloudAndOceanVisuals` only ever runs behind `if (this.activeTerrain)`.
    if (!body.def.terrain) {
      this.activeSurface = null;
      this.activeTerrain = null;
      this.activeOcean = null;
      this.activeClouds = null;
      this.activeVegetation = null;
      return;
    }

    this.ensureSurface(body);
    this.activeSurface = this.surfaces.get(body.id) ?? null;
    this.activeTerrain = this.terrainRenderers.get(body.id) ?? null;
    this.activeOcean = this.oceans.get(body.id) ?? null;
    this.activeClouds = this.clouds.get(body.id) ?? null;
    this.activeVegetation = this.vegetation.get(body.id) ?? null;
    if (this.activeSurface) this.character.setHeightField(this.activeSurface.heightField);
  }

  /**
   * Keeps the `active*` pointers aimed at whoever the camera is actually
   * looking at.
   *
   * `setActiveBody` is a latch, updated by whichever caller last moved the
   * vessel or the free camera. That is one call site per way of travelling,
   * and every one of them is a chance to forget — `restoreShip` forgot
   * entirely (see NEXT_SESSION.md), and `fastTravelTo`, `checkReferenceBody`
   * and the tour all skipped it for a body with no terrain profile. The
   * failure is the same each time and it is severe: one body's renderers fed
   * another body's camera-relative coordinates.
   *
   * A latch is still the right shape — `ensureSurface` allocates, and this
   * must not run every frame for its own sake — so this only re-latches when
   * the view body has actually changed out from under it. One string compare
   * per frame buys immunity from the whole class.
   */
  private syncActiveBody(): void {
    const view = this.viewBody;
    if (this.activeViewBodyId !== view.id) this.setActiveBody(view);
  }

  /**
   * The bodies that can plausibly be close enough to `body` to matter at
   * once: its parent and siblings if it is a moon, or itself and its moons if
   * it is a planet. Only members with a terrain profile are candidates for
   * real streamed geometry — everything else only ever gets the astronomical
   * layer's impostor.
   *
   * Every planet's `parent` is the star, not another planet — so climbing to
   * `body.parent` unconditionally would turn "a planet's moons" into "every
   * planet in the system" the moment `body` itself has no moon-hosting
   * parent. The star check is what tells a moon (parent is a planet) apart
   * from a planet (parent is the star, or there is no parent at all).
   *
   * The two cases are a union, not an either/or: a body can simultaneously be
   * a moon of something else AND have its own moons (Kolyma orbits the
   * gas giant Cyrene and is itself orbited by Rime). Picking only one "hub"
   * — as this used to — silently drops whichever set the heuristic didn't
   * pick: treating Kolyma as "a moon" because its parent isn't a star made
   * its group Cyrene's other children, and Rime, Kolyma's own moon and the
   * one actually worth promoting when the ship is near Kolyma, was never a
   * candidate at all.
   */
  private localGroupOf(body: CelestialBody): CelestialBody[] {
    const group = new Set<CelestialBody>([body, ...body.children]);
    if (body.parent && !isStarDef(body.parent.def)) {
      group.add(body.parent);
      for (const sibling of body.parent.children) group.add(sibling);
    }
    return Array.from(group);
  }

  private terrainGroupOf(body: CelestialBody): CelestialBody[] {
    return this.localGroupOf(body).filter((b) => b.def.terrain);
  }

  /**
   * The complement of `terrainGroupOf`: bodies in the same local group with a
   * real cloud deck but no terrain of their own — a gas giant. These can
   * never enter `terrainGroupOf`/`activeBodyIds` (`ensureSurface` requires
   * `def.terrain`), so without a promotion path of their own they would stay
   * the crude astronomical impostor forever, at any distance — see
   * `activeCloudOnlyIds`.
   */
  private cloudOnlyGroupOf(body: CelestialBody): CelestialBody[] {
    return this.localGroupOf(body).filter((b) => !b.def.terrain && b.atmosphere.cloudCoverage > 0);
  }

  private withinPromotionRange(
    body: CelestialBody,
    cameraPositionRoot: Vec3,
    currentlyActive: boolean,
    t: number,
  ): boolean {
    const bodyPositionRoot = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
    const distance = Vec3.subVectors(bodyPositionRoot, cameraPositionRoot).length();
    return distance < body.radius * (currentlyActive ? DEMOTE_RADII : PROMOTE_RADII);
  }

  /**
   * Decides which bodies get real streamed terrain this frame.
   *
   * The view body is always included. Its parent and siblings join it once
   * the camera is close enough — in units of *their own* radius, with
   * hysteresis against the boundary — that the astronomical layer's
   * compressed sphere would visibly diverge from real terrain. That is the
   * same reasoning behind the astronomical layer's own hand-off distance,
   * just applied per body instead of to one exclusive slot.
   */
  private updateActiveBodySet(): void {
    const viewBody = this.viewBody;
    const desired = new Set<string>();

    if (viewBody.def.terrain) desired.add(viewBody.id);

    // Deliberately not gated on `viewBody.def.terrain`: a gas giant like Aegis
    // has none itself but its moons do, and `checkViewBody`'s SOI handover
    // puts the camera "on" the gas giant the instant it drifts outside
    // whichever moon's (often small — a few body radii) sphere of influence it
    // was just near. Gating this block on the parent's own terrain used to
    // mean none of that parent's moons could ever promote once that happened,
    // no matter how close the camera actually was to one of them.
    const cameraRoot = this.system.frames.transformPosition(
      this.cameraPositionInFrame(),
      viewBody.inertialFrameId,
      this.system.root,
      this.time,
    );
    const terrainGroup = this.terrainGroupOf(viewBody);
    for (const sibling of terrainGroup) {
      if (sibling.id === viewBody.id) continue;
      const wasActive = this.activeBodyIds.has(sibling.id);
      if (this.withinPromotionRange(sibling, cameraRoot, wasActive, this.time)) {
        desired.add(sibling.id);
      }
    }

    // Gas giants (no terrain, so never eligible above) get the same
    // promotion treatment against their own cloud deck instead — see
    // `activeCloudOnlyIds`. `cloudOnlyGroupOf` already includes `viewBody`
    // itself when applicable, so unlike the terrain case above there is no
    // separate unconditional add: standing right next to a gas giant with
    // nothing to land on is still just "within promotion range of it."
    const desiredClouds = new Set<string>();
    const cloudGroup = this.cloudOnlyGroupOf(viewBody);
    for (const candidate of cloudGroup) {
      const wasActive = this.activeCloudOnlyIds.has(candidate.id);
      if (this.withinPromotionRange(candidate, cameraRoot, wasActive, this.time)) {
        desiredClouds.add(candidate.id);
      }
    }

    this.applyActiveBodySet(desired, desiredClouds);

    // Bodies outside the view body's *current* local group are not merely
    // demoted (`applyActiveBodySet` already detaches those from the scene) —
    // they are no longer even promotion candidates, so nothing will touch
    // their streamed resources again until the player travels back. Prune
    // those now rather than leaving them cached forever.
    const keepIds = new Set<string>();
    for (const b of terrainGroup) keepIds.add(b.id);
    for (const b of cloudGroup) keepIds.add(b.id);
    // A body still dissolving back into its impostor needs its terrain for a
    // few more frames. Disposing it here is what would turn the fade-out into
    // the very cut this replaces.
    for (const id of this.fadingBodyIds()) keepIds.add(id);
    this.pruneAbandonedBodies(keepIds);
  }

  /**
   * Disposes and forgets every per-body streaming/render resource for a body
   * that has left the view body's local group entirely (see `localGroupOf`)
   * — travelled away from, not just temporarily demoted while still nearby.
   *
   * `ensureSurface`/`ensureCloudDeck` cache one `PlanetSurface` /
   * `TerrainRenderer` / `OceanRenderer` / `CloudLayer` per body id and keep
   * it forever once created; demotion in `applyActiveBodySet` only detaches
   * the mesh from the scene graph, it never freed anything. Each
   * `PlanetSurface` can hold up to `quality.maxChunks` GPU-resident chunk
   * geometries (real vertex/index/normal/colour buffers), frozen at whatever
   * count they had reached the moment the body stopped updating — so every
   * distinct planet or moon visited in a session added its resident chunks
   * to the total with nothing ever subtracted, until the GPU process ran out
   * of memory and the tab crashed.
   *
   * `AtmosphereModel` is deliberately left alone: it is cheap pure-JS state
   * with no GPU resources, so dropping it here would buy no memory back.
   */
  /**
   * Takes every one of a body's render roots out of the world scene.
   *
   * Deliberately unconditional and idempotent: `THREE.Object3D.removeFromParent`
   * is a no-op on something already detached, so this is safe to call whether
   * or not `applyActiveBodySet` got there first. Whoever ends a body's life is
   * then not required to know which of the two paths ran.
   */
  private detachBodyRoots(id: string): void {
    this.terrainRenderers.get(id)?.root.removeFromParent();
    this.oceans.get(id)?.root.removeFromParent();
    this.clouds.get(id)?.root.removeFromParent();
    this.vegetation.get(id)?.root.removeFromParent();
  }

  private pruneAbandonedBodies(keepIds: ReadonlySet<string>): void {
    for (const [id, surface] of this.surfaces) {
      if (keepIds.has(id)) continue;

      // Detach before disposing, and do it here rather than trusting that
      // `applyActiveBodySet` already has.
      //
      // It very often has not. That method only detaches a demoted body once
      // its fade has reached 0, and until then the body sits in `retiring`
      // with every root still parented. The prune's own guard against
      // colliding with that is `fadingBodyIds()`, which covers `0 < fade < 1`
      // — so a body demoted on the frame its fade is still exactly 1, which is
      // every body the player jumps away from, is not protected and is pruned
      // while attached. `this.oceans.delete(id)` below then removes the only
      // handle anything had on it, so when the fade finally does reach 0
      // `applyActiveBodySet` looks the renderer up, finds nothing, and the
      // roots stay in the scene forever.
      //
      // Terrain, clouds and vegetation survive that because their `dispose`
      // empties the group, so an orphaned root draws nothing. The ocean did
      // not, and a sea-level sphere frozen at its last camera-relative offset
      // is the enormous ball that hangs in the sky after changing planet.
      this.detachBodyRoots(id);

      surface.dispose();
      this.surfaces.delete(id);

      this.terrainRenderers.get(id)?.dispose();
      this.terrainRenderers.delete(id);

      const ocean = this.oceans.get(id);
      if (ocean) {
        // Must be dropped before the ocean's own dispose, or the SSR capture
        // keeps a dead object in its exclude set forever.
        this.renderer.removeSsrExclude(ocean.root);
        ocean.dispose();
        this.oceans.delete(id);
      }

      this.clouds.get(id)?.dispose();
      this.clouds.delete(id);

      const vegetationLayer = this.vegetation.get(id);
      if (vegetationLayer) {
        this.renderer.removeSsrExclude(vegetationLayer.root);
        vegetationLayer.dispose();
        this.vegetation.delete(id);
      }
      this.readyBodyIds.delete(id);
    }

    // Gas giants are cloud-only (no `PlanetSurface`), so the loop above never
    // reaches them.
    for (const [id, clouds] of this.clouds) {
      if (keepIds.has(id)) continue;
      // Same hazard as the loop above: a cloud-only body can be pruned while
      // its deck is still parented into the world scene.
      clouds.root.removeFromParent();
      clouds.dispose();
      this.clouds.delete(id);
      this.activeCloudOnlyIds.delete(id);
    }
  }

  /**
   * Reconciles the world scene and the astronomical layer against `desired`.
   *
   * A body's astronomical impostor is hidden only once its real terrain has,
   * at some point since promotion, fully covered it
   * (`PlanetSurface.fullyStreamed`, latched into `readyBodyIds`): promotion
   * starts a body's surface streaming immediately, but the *visible* swap
   * waits for geometry to actually exist. Without that gate, the frame the
   * camera crosses the promotion threshold hides the impostor before
   * anything has been generated to replace it, and whatever sits behind the
   * resulting hole on screen — most often another body's own impostor —
   * shows through it instead. That was this bug.
   *
   * The latch, rather than re-checking every frame, is what keeps ordinary
   * streaming (a new chunk in flight as the camera moves) from flickering an
   * already-shown body's impostor back on: `fullyStreamed` goes false again
   * routinely once a body has been fully covered at least once, and none of
   * those later dips are holes — the anti-hole invariant inside
   * `PlanetSurface` already guarantees something is drawn throughout them.
   */
  private applyActiveBodySet(desired: ReadonlySet<string>, desiredClouds: ReadonlySet<string>): void {
    // Bodies leaving the active set are NOT detached straight away. A demoted
    // body still has to dissolve back into its impostor, and a root removed
    // from the scene this frame draws nothing to dissolve — which is why the
    // fade only ever worked on the way in. They stay attached, and stay in
    // `activeBodyIds` so `render` keeps positioning and lighting them, until
    // `updateSurfaceFades` has driven them to zero.
    const retiringClouds = new Set<string>();
    for (const id of this.activeCloudOnlyIds) {
      if (desiredClouds.has(id)) continue;
      if ((this.surfaceFades.get(id) ?? 0) > 0) {
        retiringClouds.add(id);
        continue;
      }
      const clouds = this.clouds.get(id);
      if (clouds && clouds.root.parent === this.renderer.worldScene) {
        this.renderer.worldScene.remove(clouds.root);
      }
    }
    for (const id of desiredClouds) {
      if (this.activeCloudOnlyIds.has(id)) continue;
      const body = this.system.find(id);
      if (!body) continue;
      this.ensureCloudDeck(body);
      const clouds = this.clouds.get(id);
      if (clouds && clouds.root.parent !== this.renderer.worldScene) {
        this.renderer.worldScene.add(clouds.root);
      }
    }
    this.desiredCloudOnlyIds = new Set(desiredClouds);
    this.activeCloudOnlyIds = new Set([...desiredClouds, ...retiringClouds]);

    const retiring = new Set<string>();
    for (const id of this.activeBodyIds) {
      if (desired.has(id)) continue;
      // Clears the readiness latch first, which is what turns the fade's target
      // to zero; the detach below then waits for it to get there.
      this.readyBodyIds.delete(id);
      if ((this.surfaceFades.get(id) ?? 0) > 0) {
        retiring.add(id);
        continue;
      }
      const terrain = this.terrainRenderers.get(id);
      if (terrain && terrain.root.parent === this.renderer.worldScene) {
        this.renderer.worldScene.remove(terrain.root);
      }
      const ocean = this.oceans.get(id);
      if (ocean && ocean.root.parent === this.renderer.worldScene) {
        this.renderer.worldScene.remove(ocean.root);
      }
      const clouds = this.clouds.get(id);
      if (clouds && clouds.root.parent === this.renderer.worldScene) {
        this.renderer.worldScene.remove(clouds.root);
      }
      const vegetation = this.vegetation.get(id);
      if (vegetation && vegetation.root.parent === this.renderer.worldScene) {
        this.renderer.worldScene.remove(vegetation.root);
      }
    }

    for (const id of desired) {
      if (this.activeBodyIds.has(id)) continue;
      const body = this.system.find(id);
      if (!body) continue;
      this.ensureSurface(body);
      const terrain = this.terrainRenderers.get(id);
      if (terrain && terrain.root.parent !== this.renderer.worldScene) {
        this.renderer.worldScene.add(terrain.root);
      }
      const clouds = this.clouds.get(id);
      if (clouds && clouds.root.parent !== this.renderer.worldScene) {
        this.renderer.worldScene.add(clouds.root);
      }
      const ocean = this.oceans.get(id);
      if (ocean && ocean.root.parent !== this.renderer.worldScene) {
        this.renderer.worldScene.add(ocean.root);
      }
      const vegetation = this.vegetation.get(id);
      if (vegetation && vegetation.root.parent !== this.renderer.worldScene) {
        this.renderer.worldScene.add(vegetation.root);
      }
    }

    this.activeBodyIds = new Set([...desired, ...retiring]);

    for (const id of desired) {
      if (this.readyBodyIds.has(id)) continue;
      if (this.surfaces.get(id)?.fullyStreamed) this.readyBodyIds.add(id);
    }
    // The impostor is NOT hidden the moment the terrain is ready. It has to
    // stay drawn for as long as the terrain is still dithering in over it —
    // the cross-fade works precisely because the astronomical pass has already
    // laid the impostor into the colour buffer that the terrain's discarded
    // fragments leave untouched. See `updateSurfaceFades`.
    const fadedIn = new Set<string>();
    for (const id of [...this.readyBodyIds, ...this.desiredCloudOnlyIds]) {
      if ((this.surfaceFades.get(id) ?? 0) >= 1) fadedIn.add(id);
    }
    this.astronomical.hiddenBodyIds = fadedIn;
  }

  // =========================================================================
  // Loop
  // =========================================================================

  start(): void {
    this.lastRealTime = performance.now();
    const loop = (now: number): void => {
      if (this.disposed) return;
      this.rafHandle = requestAnimationFrame(loop);
      const realDelta = Math.min(0.25, (now - this.lastRealTime) / 1000);
      this.lastRealTime = now;
      this.frameUpdate(realDelta);
    };
    this.rafHandle = requestAnimationFrame(loop);
  }

  stop(): void {
    cancelAnimationFrame(this.rafHandle);
  }

  private frameUpdate(realDelta: number): void {
    this.frame++;
    this.fpsAccumulator += realDelta;
    this.fpsFrames++;
    if (this.fpsAccumulator >= 0.5) {
      this.fps = this.fpsFrames / this.fpsAccumulator;
      this.fpsAccumulator = 0;
      this.fpsFrames = 0;
    }

    this.resizeIfNeeded();
    this.input.update(realDelta);
    this.handleActions();
    // Before anything reads `activeTerrain`/`activeSurface` this frame.
    this.syncActiveBody();
    this.updateSurfaceFades(realDelta);

    if (!this.paused) {
      const physicsStart = performance.now();
      this.simulate(realDelta);
      this.physicsMs = performance.now() - physicsStart;
    }

    // The free camera moves outside the physics step on purpose: it is not part
    // of the simulation and must not be able to affect it.
    if (this.tourActive) {
      this.tour.update(realDelta);
    } else if (this.freeFlight.active) {
      // Ceiling from how close the camera is to the ground it is looking at.
      // Applied before the camera moves, so the limit describes where it is
      // rather than where it just arrived from — at high speed those are very
      // different places.
      this.freeFlight.speedLimit = FreeFlightCamera.proximitySpeedLimit(
        this.cameraAltitude(this.cameraPositionInFrame()),
      );
      const freeFlightRotation = Vec3.addVectors(this.input.axes.rotation, this.input.axes.mouseStick).clampLength(
        1,
      );
      const freeFlightTranslation = this.input.axes.translation;
      const freeFlightWheel = this.input.axes.wheel;
      this.freeFlight.update(
        freeFlightRotation,
        freeFlightTranslation,
        this.input.isDown('boost'),
        freeFlightWheel,
        realDelta,
      );
      this.updateFreeFlightAutosave(freeFlightRotation, freeFlightTranslation, freeFlightWheel, realDelta);
    }

    this.updateStreaming();
    this.render();
    this.updateAudio(realDelta);

    this.snapshotTimer += realDelta;
    if (this.snapshotTimer >= SNAPSHOT_INTERVAL) {
      this.snapshotTimer = 0;
      this.publishSnapshot();
    }

    this.input.endFrame();
  }

  /**
   * Advances simulated time.
   *
   * Below the analytical threshold this runs full physics sub-steps. Above it,
   * the vessel is propagated analytically, which is what makes a 10,000x
   * cruise cost the same per frame as real time.
   */
  private simulate(realDelta: number): void {
    const body = this.ship.referenceBody;
    const warpContext = this.buildWarpContext();
    const step = this.warp.step(realDelta, warpContext, PHYSICS_STEP);

    if (step.autoStopReason) {
      this.notify('warning', 'Time warp reduced', step.autoStopReason);
    }

    if (step.surfaceHold) {
      // Parked, with time running faster than physics can follow. The vessel
      // rides the planet: its position and attitude are constant in the
      // planet-fixed frame, and only the frame itself turns.
      //
      // This has to be done explicitly. Terrain contact is what normally holds a
      // landed vessel against a rotating surface, and that is a per-step force;
      // with the steps skipped, a vessel stored in inertial coordinates would
      // stay at a fixed point in space while the ground rotated out from under
      // it, and would be underground within a minute of simulated time.
      const dt = step.simulationDelta;
      const fixed = this.system.frames.transformState(
        this.ship.body.position,
        this.ship.body.velocity,
        body.inertialFrameId,
        body.fixedFrameId,
        this.time,
      );
      const fixedOrientation = this.system.frames.transformOrientation(
        this.ship.body.orientation,
        body.inertialFrameId,
        body.fixedFrameId,
        this.time,
      );
      // At rest relative to the ground, which is what "parked" means. Anything
      // it had left is residual settling noise, and carrying it forward across a
      // large time step would integrate that noise into a real displacement.
      fixed.velocity.set(0, 0, 0);

      this.time += dt;

      const restored = this.system.frames.transformState(
        fixed.position,
        fixed.velocity,
        body.fixedFrameId,
        body.inertialFrameId,
        this.time,
      );
      this.ship.body.position.copy(restored.position);
      this.ship.body.velocity.copy(restored.velocity);
      this.ship.body.orientation.copy(
        this.system.frames.transformOrientation(
          fixedOrientation,
          body.fixedFrameId,
          body.inertialFrameId,
          this.time,
        ),
      );
      return;
    }

    if (step.analytical) {
      // Analytical propagation. Only valid because the warp controller has
      // already verified there is no thrust, no atmosphere and no contact.
      const dt = step.simulationDelta;
      const sv = this.warp.propagateAnalytically(this.ship, body, this.time, dt);
      this.ship.body.position.copy(sv.position);
      this.ship.body.velocity.copy(sv.velocity);
      this.ship.body.orientation.integrate(this.ship.body.angularVelocity, dt);
      this.time += dt;
      this.checkReferenceBody();
      return;
    }

    // The accumulator owns the leftover time between frames, and its return
    // value is the number of whole steps to run. Recomputing the count by
    // rounding the frame's delta instead — which is what this did — throws that
    // remainder away every frame, and rounds to *zero* whenever a frame is
    // shorter than half a step. On a 120 or 144 Hz display at 1x that is every
    // frame, so the simulation never advanced at all: the game sat frozen until
    // time warp multiplied the delta far enough to round up to one.
    const steps = this.accumulator.advance(step.simulationDelta);
    for (let i = 0; i < steps; i++) {
      this.physicsStep(PHYSICS_STEP);
    }
  }

  private physicsStep(dt: number): void {
    const env = this.buildEnvironment();

    if (this.onFoot) {
      this.stepCharacter(env, dt);
    } else {
      // Free flight detaches the camera "entirely" per its own doc comment,
      // but the stick/keyboard axes it reads to fly the camera are the same
      // ones this block reads to fly the ship — without this gate, moving
      // the free camera also fires RCS and the main engine. Macros and
      // manoeuvre-node execution are deliberately left running regardless
      // (watching an in-progress landing macro from outside is exactly what
      // free flight is for); only *manual* stick/throttle input is cut.
      const manualControl = !this.freeFlight.active;

      // Macros first: they set hold modes and throttle, which the flight
      // computer then turns into actuator commands. A manual stick deflection
      // pauses a running macro, so free flight also has to report zero input
      // magnitude here — otherwise moving the camera to watch a macro fly
      // would pause the very macro being watched.
      const macroContext = this.macroContext(env, dt);
      this.macros.update(macroContext, manualControl ? this.input.inputMagnitude : 0);

      const pilot = {
        // Mouse and keyboard add: the mouse is the primary aiming device and
        // the arrow keys are there for fine work, so a pilot can trim with one
        // while steering with the other.
        rotation: manualControl
          ? Vec3.addVectors(this.input.axes.rotation, this.input.axes.mouseStick).clampLength(1)
          : new Vec3(),
        translation: manualControl ? this.input.axes.translation.clone() : new Vec3(),
        // Whichever of the momentary and cruise demands is asking for more.
        throttle: this.macros.isRunning
          ? this.ship.controls.throttle
          : manualControl
            ? Math.max(this.input.axes.throttle, this.input.axes.thrust)
            : 0,
      };
      if (manualControl && !this.macros.isRunning && this.input.axes.thrust > 0.01) {
        this.ship.controls.mainEngineArmed = true;
      }
      this.computer.update(this.ship, env, pilot, dt);
      this.ship.step(dt, env);

      // Manoeuvre node execution outside a macro.
      if (this.planner.executingNodeId && !this.macros.isRunning) {
        const result = this.planner.updateExecution(this.ship, this.ship.referenceBody, this.time, dt);
        if (result.direction) {
          this.computer.maneuverDirection = result.direction;
          this.computer.setAlignment('maneuver');
          this.ship.controls.mainEngineArmed = result.throttle > 0;
          this.ship.controls.throttle = result.throttle;
        }
      }
    }

    this.time += dt;
    this.checkReferenceBody();
    this.checkVesselHazards();
  }

  private stepCharacter(env: FlightEnvironment, dt: number): void {
    const surface = this.activeSurface;
    if (!surface) return;
    this.character.step(
      {
        // The translation axes are in *vessel* body axes, where +X is port.
        // A walking character's right hand is the opposite sign.
        move: new Vec3(-this.input.axes.translation.x, 0, this.input.axes.translation.z),
        lookYaw: this.input.axes.lookYaw,
        lookPitch: this.input.axes.lookPitch,
        jump: this.input.isDown('jump'),
        sprint: this.input.isDown('sprint'),
        crouch: this.input.isDown('crouch'),
      },
      env.gravity.length(),
      env.density,
      dt,
    );
    // `axes.lookYaw`/`lookPitch` are a one-shot mouse delta for the whole
    // rendered frame, reset once by `input.update()` -- but this method runs
    // once per PHYSICS substep, and the fixed-step accumulator runs more than
    // one substep per frame whenever the frame is slow. Left unconsumed, a
    // slow frame reapplied the same look delta to `heading`/`pitch` once per
    // substep, so the same mouse movement turned the camera further the
    // choppier the framerate got -- reported as look needing a slow, careful
    // mouse hand to feel right at all. Zeroing here means only the first
    // substep of a frame ever sees a nonzero delta; every later one this
    // frame reads zero, same as if there were only one substep in the first
    // place.
    this.input.axes.lookYaw = 0;
    this.input.axes.lookPitch = 0;

    // The ship still needs stepping so it settles on its gear and burns nothing.
    this.ship.step(dt, env);
  }

  /**
   * Assembles everything the vessel needs to know about its surroundings.
   * Built once per physics step; nothing downstream queries the world directly.
   */
  private buildEnvironment(): FlightEnvironment {
    const body = this.ship.referenceBody;
    const t = this.time;
    const position = this.ship.body.position;

    const gravity = this.gravity.accelerationInBodyFrame(body, position, t);
    const radius = position.length();
    const up = radius > 0 ? position.clone().mulScalar(1 / radius) : new Vec3(0, 1, 0);

    const positionFixed = this.system.frames.transformPosition(position, body.inertialFrameId, body.fixedFrameId, t);

    const surface = this.surfaces.get(body.id);
    const terrainHeight = surface ? surface.heightField.elevationAtPosition(positionFixed) : 0;
    const surfaceRadius = surface ? surface.heightField.walkableRadiusAt(positionFixed) : body.radius;
    const altitude = radius - surfaceRadius;
    const altitudeAboveDatum = radius - body.radius;

    const atmosphere = this.atmospheres.get(body.id) ?? new AtmosphereModel(body);
    const latitudeFactor = Math.abs(clamp(up.y, -1, 1));
    const sunDirection = this.sunDirectionAt(body, position, t);
    const solarFactor = clamp01(up.dot(sunDirection) * 0.5 + 0.5);

    const density = atmosphere.densityAt(altitudeAboveDatum);
    const pressure = atmosphere.pressureAt(altitudeAboveDatum);
    const temperature = atmosphere.temperatureAt(altitudeAboveDatum, latitudeFactor, solarFactor);
    const speedOfSound = atmosphere.speedOfSoundAt(altitudeAboveDatum, latitudeFactor, solarFactor);

    // Air velocity: co-rotation plus local wind, expressed in the inertial frame.
    const windFixed = atmosphere.windAt(positionFixed, t);
    const windInertial = this.system.frames.transformDirection(windFixed, body.fixedFrameId, body.inertialFrameId, t);
    const airVelocity = body.surfaceVelocityAt(position).add(windInertial);

    const irradiance = this.irradianceAt(body, position, t);

    return {
      t,
      body,
      gravity,
      up,
      terrainHeight,
      altitude,
      altitudeAboveDatum,
      density,
      pressure,
      temperature,
      speedOfSound,
      airVelocity,
      irradiance,
      positionFixed,
      sampleTerrainRadius: (posInFrame: Vec3): number => {
        if (!surface) return body.radius;
        const fixed = this.system.frames.transformPosition(posInFrame, body.inertialFrameId, body.fixedFrameId, t);
        return surface.heightField.walkableRadiusAt(fixed);
      },
      gravityAt: (posInFrame: Vec3, out: Vec3): Vec3 =>
        this.gravity.accelerationInBodyFrame(body, posInFrame, t, out),
    };
  }

  private macroContext(env: FlightEnvironment, dt: number): MacroContext {
    return {
      ship: this.ship,
      computer: this.computer,
      planner: this.planner,
      predictor: this.predictor,
      body: this.ship.referenceBody,
      env,
      t: this.time,
      dt,
      // Resolved live, not snapshotted: a macro flying to another body has to
      // follow where that body is now.
      target: this.targetBodyId ? (this.system.find(this.targetBodyId) ?? null) : null,
      terrainCeilingFor: (body: CelestialBody) => this.surfaces.get(body.id)?.heightField.ceiling ?? 0,
    };
  }

  /** Stellar irradiance at a position in a body's frame, W/m^2. */
  private irradianceAt(body: CelestialBody, positionInBodyFrame: Vec3, t: number): number {
    const starPos = this.system.frames.stateInRoot(this.system.star.inertialFrameId, t).position;
    const bodyPos = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
    const world = Vec3.addVectors(bodyPos, positionInBodyFrame);
    const d = Math.max(this.system.star.radius, world.distanceTo(starPos));
    const luminosity = (this.system.star.def as { luminosity?: number }).luminosity ?? 4.2e26;
    return luminosity / (4 * Math.PI * d * d);
  }

  /**
   * Planetshine: nearby bodies treated as area lights.
   *
   * The dark side of a moon beside its planet is not black, and neither is a
   * slope facing away from the sun on a world with a big neighbour. Until now
   * the only fill light was `haze * skyStrength`, which folds in atmospheric
   * density — so on a thin-aired world any surface out of direct sun had
   * essentially no fill at all. That is defensible physics and an ugly picture,
   * and it is what NEXT_SESSION 8.3 describes as Vesper's near-field going
   * black while everything past it is correctly lit.
   *
   * This is cheap and physically grounded rather than a fudge factor. A body of
   * radius R at distance d subtends a solid angle of about pi(R/d)^2. Its
   * surface reflects the starlight falling on it as a Lambertian, giving a
   * radiance of E_star * albedo / pi. Irradiance here is the product, so the pi
   * cancels:
   *
   *     E = E_star_at_body * albedo * (R/d)^2 * litFraction
   *
   * `litFraction` is the phase term: we see the neighbour's lit face at
   * opposition and its dark one at conjunction, which is the difference between
   * a full moon and a new one. Only the parent, the children and the siblings
   * are considered — anything further is orders of magnitude down by (R/d)^2
   * and not worth the transform.
   *
   * Returned intensity is in the same normalised units as `sunIntensity`, so
   * the shaders can add it to what they already do.
   */
  private planetshineAt(
    body: CelestialBody,
    cameraPosition: Vec3,
    t: number,
    outDirection: Vec3,
    outColor: THREE.Color,
  ): number {
    const star = this.system.star;
    const cameraRoot = this.system.frames.transformPosition(
      cameraPosition,
      body.inertialFrameId,
      this.system.root,
      t,
    );
    const starRoot = this.system.frames.stateInRoot(star.inertialFrameId, t).position;

    // Parent, children and siblings. A Set keeps a sibling list that includes
    // this body from double-counting it, and the body itself is skipped: the
    // ground underfoot is not its own fill light.
    const candidates = new Set<CelestialBody>();
    if (body.parent) {
      candidates.add(body.parent);
      for (const sibling of body.parent.children) candidates.add(sibling);
    }
    for (const child of body.children) candidates.add(child);

    outDirection.set(0, 0, 0);
    outColor.setRGB(0, 0, 0);
    let total = 0;

    for (const other of candidates) {
      if (other.id === body.id || other.id === star.id) continue;

      const otherRoot = this.system.frames.stateInRoot(other.inertialFrameId, t).position;
      const toOther = Vec3.subVectors(otherRoot, cameraRoot);
      const d = toOther.length();
      if (d <= other.radius) continue;

      // Phase angle measured at the neighbour, between us and the star.
      const toStarFromOther = Vec3.subVectors(starRoot, otherRoot).normalize();
      const toUsFromOther = Vec3.subVectors(cameraRoot, otherRoot).normalize();
      const litFraction = clamp01(toStarFromOther.dot(toUsFromOther) * 0.5 + 0.5);
      if (litFraction <= 0.001) continue;

      const ratio = other.radius / d;
      const albedo = discAlbedo(other.def);
      const scalarAlbedo = (albedo.r + albedo.g + albedo.b) / 3;
      const irradianceHere =
        this.irradianceAt(other, new Vec3(), t) * scalarAlbedo * ratio * ratio * litFraction;
      if (irradianceHere <= 0) continue;

      // All inertial frames share their axes with the root frame, so a
      // root-frame direction needs no conversion to be used in the body frame.
      const weight = irradianceHere;
      toOther.normalize();
      outDirection.x += toOther.x * weight;
      outDirection.y += toOther.y * weight;
      outDirection.z += toOther.z * weight;
      outColor.r += albedo.r * weight;
      outColor.g += albedo.g * weight;
      outColor.b += albedo.b * weight;
      total += irradianceHere;
    }

    if (total <= 0) {
      outDirection.set(0, 1, 0);
      return 0;
    }

    outDirection.normalize();
    // Colour is the irradiance-weighted mean neighbour albedo, renormalised so
    // it carries hue only; brightness is the returned intensity.
    outColor.multiplyScalar(1 / total);
    const peak = Math.max(outColor.r, outColor.g, outColor.b);
    if (peak > 0) outColor.multiplyScalar(1 / peak);

    return (total / 1361) * 3.2;
  }

  /**
   * Finds whichever nearby body currently overlaps the star's disc as seen
   * from `body`'s centre, if any — the condition for an eclipse. Only the
   * parent/children/siblings candidate set `planetshineAt` gathers is
   * considered: the astronomical layer renders anything farther as a
   * compressed impostor with no shared frustum to shadow through, so an
   * eclipse there would not be meaningful even if the alignment happened.
   *
   * Returns the occluder's centre and radius in `body`'s FIXED frame,
   * relative to `body`'s own centre — the same axes `setCloudShadow` already
   * uses for `uSunDirectionFixed` — or null when nothing currently overlaps.
   * Only ever one occluder considered at a time, same as the cloud shadow's
   * single deck.
   */
  private eclipseOccluderAt(body: CelestialBody, t: number): { centreFixed: Vec3; radius: number } | null {
    const star = this.system.star;
    const bodyRoot = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
    const starRoot = this.system.frames.stateInRoot(star.inertialFrameId, t).position;
    const toStar = Vec3.subVectors(starRoot, bodyRoot);
    const starDist = toStar.length();
    if (starDist <= 0) return null;
    const starDir = toStar.normalized();
    const starAngularRadius = Math.asin(clamp01(star.radius / starDist));

    const candidates = new Set<CelestialBody>();
    if (body.parent) {
      candidates.add(body.parent);
      for (const sibling of body.parent.children) candidates.add(sibling);
    }
    for (const child of body.children) candidates.add(child);

    let best: CelestialBody | null = null;
    let bestSeparation = Infinity;
    let bestRoot: Vec3 | null = null;

    for (const other of candidates) {
      if (other.id === body.id || other.id === star.id) continue;
      const otherRoot = this.system.frames.stateInRoot(other.inertialFrameId, t).position;
      const toOther = Vec3.subVectors(otherRoot, bodyRoot);
      const dist = toOther.length();
      if (dist <= other.radius) continue;

      // Angular-disc overlap test: does this candidate's silhouette, as seen
      // from `body`, overlap the star's? Both angular radii are small
      // (asin of a ratio well under 1 in every real case here), but asin is
      // exact and cheap, so there is no reason to small-angle-approximate.
      const otherDir = toOther.normalized();
      const otherAngularRadius = Math.asin(clamp01(other.radius / dist));
      const separation = Math.acos(clamp(otherDir.dot(starDir), -1, 1));
      if (separation > starAngularRadius + otherAngularRadius) continue;
      if (separation < bestSeparation) {
        bestSeparation = separation;
        best = other;
        bestRoot = otherRoot;
      }
    }

    if (!best || !bestRoot) return null;

    // `transformDirection` only rotates; it works on any relative vector; not
    // just a unit direction, and `bodyRoot`-relative axes already match
    // `body.inertialFrameId`'s (inertial frames share the root's axes, they
    // only translate) so no root-frame conversion step is needed first.
    const relativeRoot = Vec3.subVectors(bestRoot, bodyRoot);
    const centreFixed = this.system.frames.transformDirection(
      relativeRoot,
      body.inertialFrameId,
      body.fixedFrameId,
      t,
    );
    return { centreFixed, radius: best.radius };
  }

  /**
   * Factor (0..1) by which terrain hides the star's disc as seen from the
   * camera, complementing the reference body's analytic sphere test in
   * `render`. That test knows only the world's mean radius; this re-runs the
   * same ray against the actual heightfield, so a mountain ridge between the
   * camera and a low sun occludes the glare the way the horizon does — the
   * case the sphere term cannot see, because the sun can be geometrically
   * "up" while a nearer ridge is still in the way.
   *
* The camera→star ray is marched through the heightfield's shell in the
    * body's fixed frame. Each sample where the ray drops below the walkable
    * surface measures the angular height of that silhouette above the line to
    * the star's centre (`-altitude / distance`); the deepest such drop, compared
    * against the star's true angular radius, says what fraction of the star's
    * disc the ridge swallows — the glare dies as the ridge covers the disc, not
    * some metres-of-depth behind it.
   *
   * Two geometry notes, both the standard planetary-scale traps:
   *   - The ray's closest approach to the centre comes from the cross product
   *     `cam x sun` rather than `sqrt(cam^2 - along^2)`, so no two near-equal
   *     ~Rmax^2 terms are ever subtracted.
   *   - The shell entry/exit use the factored form `(Rmax - perp)*(Rmax + perp)`
   *     for the same reason.
   */
  private terrainGlareOcclusion(
    body: CelestialBody,
    cameraPosition: Vec3,
    sunDirection: Vec3,
    t: number,
    starAngularRadius: number,
  ): number {
    const surface = this.surfaces.get(body.id);
    if (!surface) return 1;
    const hf = surface.heightField;

    const camFixed = this.system.frames.transformPosition(
      cameraPosition,
      body.inertialFrameId,
      body.fixedFrameId,
      t,
    );
    const sunFixed = this.system.frames.transformDirection(
      sunDirection,
      body.inertialFrameId,
      body.fixedFrameId,
      t,
    );

    const Rmax = hf.radius + hf.ceiling;
    const alongFixed = camFixed.dot(sunFixed);
    const perpendicular = Vec3.crossVectors(camFixed, sunFixed).length();
    if (perpendicular >= Rmax) return 1;

    const disc = Math.sqrt(Math.max(0, (Rmax - perpendicular) * (Rmax + perpendicular)));
    const sStart = Math.max(0, -alongFixed - disc);
    const sEnd = -alongFixed + disc;
    if (sEnd <= sStart) return 1;

    // The camera is the beam's origin (p(0) = camFixed), so the ray parameter
    // s is also the distance from the camera along the sightline — the exact
    // denominator the angular occlusion needs. March the same shell chord as
    // the body sphere test, but against the real heightfield. Samples where the
    // ray drops below the walkable surface (altitude < 0) measure how high the
    // silhouette stands above the line to the star's centre, in metres, at a
    // known distance; in half-arcminutes that *is* the occlusion.
    //
    // The fade is ANGLING, not a metre depth: the glare is a camera-side
    // artefact of the starlight, so it is covered exactly when the ridge
    // swallows the star's true disc (starAngularRadius), the same coverage
    // geometry `distantBodyGlareOcclusion` uses for transiting bodies. A fixed
    // metre fade can never track that — a 250 m of depth is a wide angle near a
    // ridge the camera is close to and a tiny one far away, which is why the
    // glare used to linger (as an orbital "offset") long past the edge.
    //
    // The crossing is short where it matters (near the limb a grazing ray clips
    // the shell thin, so the samples land close together there): 96 samples
    // keeps a thin ridge from being skipped between two widely-spaced points.
    const STEPS = 96;
    const step = (sEnd - sStart) / STEPS;
    const p = new Vec3();
    let maxAngular = 0;
    for (let i = 0; i < STEPS; i++) {
      const s = sStart + step * i;
      if (s <= 0) continue;
      p.copy(camFixed).addScaled(sunFixed, s);
      const alt = hf.altitudeAt(p);
      if (alt < 0) {
        const angular = -alt / s;
        if (angular > maxAngular) maxAngular = angular;
      }
    }
    if (maxAngular <= 0) return 1;
    if (starAngularRadius <= 0) return 0;
    const coverage = clamp01((starAngularRadius + maxAngular) / (2 * starAngularRadius));
    return 1 - coverage;
  }

  /**
   * Factor (0..1) by which a distant body transiting the star's disc dims the
   * glare, seen from the camera. `eclipseOccluderAt` only ever considers the
   * local parent/children/siblings, because a shadow can only fall on things
   * that share a render depth with the occluder; the glare is a billboard
   * drawn behind everything, so ANY body between the camera and the star
   * hides the disc, no matter how far away it is — a moon crossing the sun
   * from a neighbouring planet's surface, or a planet crossing it in deep
   * space. The astronomical layer compresses those bodies into the same depth
   * range as the star, so what matters here is angular overlap, measured
   * against the star's TRUE angular radius (the same value sizes the glare's
   * disc).
   *
   * Coverage is computed per body and the deepest taken: the fraction of the
   * star's disc swallowed by a silhouette, 0 at first contact and 1 once the
   * body's disc covers the star's.
   */
  private distantBodyGlareOcclusion(
    body: CelestialBody,
    cameraPosition: Vec3,
    sunDirection: Vec3,
    t: number,
    starAngularRadius: number,
  ): number {
    if (starAngularRadius <= 0) return 1;
    const bodyRoot = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
    const starRoot = this.system.frames.stateInRoot(this.system.star.inertialFrameId, t).position;
    const cameraRoot = Vec3.addVectors(bodyRoot, cameraPosition);
    const starDistance = Math.max(this.system.star.radius, cameraRoot.distanceTo(starRoot));

    let maxCoverage = 0;
    for (const other of this.system.bodies) {
      if (other.id === body.id || other.id === this.system.star.id) continue;
      const otherRoot = this.system.frames.stateInRoot(other.inertialFrameId, t).position;
      // Camera → body centre. Inertial frames share the root's axes (they only
      // translate), so the root-frame subtraction is exactly a camera-relative
      // vector in `cameraPosition`'s frame; `cameraPosition` is body-relative.
      const toOther = Vec3.subVectors(otherRoot, bodyRoot).sub(cameraPosition);
      const dist = toOther.length();
      // A body farther than the star is BEHIND it — the disc overlap is real,
      // but the body is backlit, not in front, so it cannot hide the glare.
      if (dist <= other.radius || dist >= starDistance) continue;
      const otherDir = toOther.normalized();
      const otherAngularRadius = Math.asin(clamp01(other.radius / dist));
      const separation = Math.acos(clamp(otherDir.dot(sunDirection), -1, 1));
      if (separation > otherAngularRadius + starAngularRadius) continue;
      const coverage = clamp01(
        (otherAngularRadius + starAngularRadius - separation) / (2 * starAngularRadius),
      );
      if (coverage > maxCoverage) maxCoverage = coverage;
    }
    return 1 - maxCoverage;
  }

  /** Unit vector toward the star, in a body's inertial frame. */
  private sunDirectionAt(body: CelestialBody, positionInBodyFrame: Vec3, t: number, out = new Vec3()): Vec3 {
    const starPos = this.system.frames.stateInRoot(this.system.star.inertialFrameId, t).position;
    const bodyPos = this.system.frames.stateInRoot(body.inertialFrameId, t).position;
    const world = Vec3.addVectors(bodyPos, positionInBodyFrame);
    return Vec3.subVectors(starPos, world, out).normalize();
  }

  /**
   * Switches the reference body when the vessel crosses into another sphere of
   * influence. The state is converted with the full rotating-frame transform, so
   * the trajectory is continuous through the change: no velocity discontinuity,
   * no visible jump.
   */
  private checkReferenceBody(): void {
    const current = this.ship.referenceBody;
    const rootPos = this.system.frames.transformPosition(
      this.ship.body.position,
      current.inertialFrameId,
      this.system.root,
      this.time,
    );
    const containing = this.system.bodyContaining(rootPos, this.time) ?? this.system.star;
    if (containing.id === current.id) return;

    const converted = this.system.frames.transformState(
      this.ship.body.position,
      this.ship.body.velocity,
      current.inertialFrameId,
      containing.inertialFrameId,
      this.time,
    );
    const orientation = this.system.frames.transformOrientation(
      this.ship.body.orientation,
      current.inertialFrameId,
      containing.inertialFrameId,
      this.time,
    );
    const angular = this.system.frames.transformAngularVelocity(
      this.ship.body.angularVelocity,
      current.inertialFrameId,
      containing.inertialFrameId,
      this.time,
    );

    this.floatingOrigin.setActiveFrame(containing.inertialFrameId, this.ship.body.position, this.time);
    this.ship.rebaseTo(containing, converted.position, converted.velocity, orientation, angular);

    this.setActiveBody(containing);

    if (!this.visitedBodies.has(containing.id)) {
      this.visitedBodies.add(containing.id);
      this.notify('success', 'New sphere of influence', `Now under the influence of ${containing.name}.`);
    } else {
      this.notify('info', 'Reference frame change', `Now referenced to ${containing.name}.`);
    }
  }

  /** Warnings that need the player's attention regardless of what they are doing. */
  private checkVesselHazards(): void {
    const tel = this.ship.telemetry();
    if (tel.hullTemperature > this.ship.thermal.temperatureLimit * 0.85 && this.frame % 60 === 0) {
      this.notify('critical', 'Hull temperature critical', `${tel.hullTemperature.toFixed(0)} K and rising.`);
    }
    if (tel.propellantFraction < 0.05 && tel.propellantFraction > 0 && this.frame % 240 === 0) {
      this.notify('warning', 'Propellant low', `${(tel.propellantFraction * 100).toFixed(1)}% remaining.`);
    }
    if (this.ship.power.chargeFraction < 0.1 && this.frame % 240 === 0) {
      this.notify('warning', 'Battery low', 'Electrical reserve below 10%.');
    }
  }

  // =========================================================================
  // Streaming and rendering
  // =========================================================================

  /**
   * The body the viewer is at, which is not always the body the vessel is at.
   *
   * Physics has one notion of "where we are" — the vessel's reference body, the
   * thing whose gravity it is under — and rendering has another: whatever world
   * the camera is looking at from close range. Those are the same object right
   * up until the camera detaches, and then they are not.
   *
   * Keeping only the vessel's meant the free camera could fly to another planet
   * and arrive at nothing. Streaming asked the vessel's body which chunks to
   * load, so the target was never built at true scale and stayed the compressed
   * low-resolution disc the astronomical layer draws; the atmosphere, ocean and
   * lighting all belonged to the world left behind. Approaching a planet in
   * free flight simply did not resolve it, however close you got.
   */
  private get viewBody(): CelestialBody {
    return this.freeFlight.active && this.freeFlightBody
      ? this.freeFlightBody
      : this.ship.referenceBody;
  }

  /**
   * Moves the free camera's frame when it crosses into another sphere of
   * influence — the camera's counterpart to `checkReferenceBody`.
   *
   * The vessel must NOT be rebased here. It is somewhere else entirely, under
   * its own gravity, and dragging its frame around because the camera went
   * sightseeing would corrupt the trajectory. Only the camera's own position is
   * converted, and because the conversion is exact the view does not jump.
   */
  /**
   * Converts the free camera between the view body's inertial and fixed frames.
   *
   * The conversion is exact and applied to orientation as well as position, so
   * crossing between the two navigation modes does not move or turn the view by
   * so much as a pixel — the camera is described differently, not placed
   * differently.
   */
  private setFreeFlightFrame(next: 'inertial' | 'fixed'): void {
    const body = this.freeFlightBody;
    if (!body || next === this.freeFlightFrame) return;
    const from = this.freeFlightFrame === 'fixed' ? body.fixedFrameId : body.inertialFrameId;
    const to = next === 'fixed' ? body.fixedFrameId : body.inertialFrameId;
    this.freeFlight.position.copy(
      this.system.frames.transformPosition(this.freeFlight.position, from, to, this.time),
    );
    this.freeFlight.orientation.copy(
      this.system.frames.transformOrientation(this.freeFlight.orientation, from, to, this.time),
    );
    this.freeFlightFrame = next;
  }

  /**
   * Chooses the navigation mode from how close the camera is to the surface.
   *
   * The atmosphere is the natural boundary where there is one — "within the
   * atmosphere" is exactly when holding station over the ground is what you
   * want. Airless bodies have no such boundary but the same problem, so they
   * get a fraction of their radius instead.
   *
   * The band is hysteretic. Without it the mode flips every frame while
   * hovering at the boundary, and every flip is a frame conversion.
   */
  private updateFreeFlightFrame(): void {
    // Tour mode picks its own frame at each phase transition; the altitude
    // hysteresis below is for manual free flight only and would fight it.
    if (this.tourActive) return;
    if (!this.freeFlight.active || !this.freeFlightBody) {
      this.freeFlightFrame = 'inertial';
      return;
    }
    const body = this.freeFlightBody;
    // Where there is air, the top of it is the boundary — being inside the
    // atmosphere is exactly when holding station over the ground is what you
    // want, and above it you are in space and the world turning beneath you is
    // the truth. Airless bodies have no such line but the same problem, so they
    // get a fraction of their radius.
    const band = body.hasAtmosphere ? body.atmosphereTop : body.radius * 0.05;
    const altitude = this.cameraAltitude(this.cameraPositionInFrame());
    const threshold = this.freeFlightFrame === 'fixed' ? band * 1.3 : band;
    this.setFreeFlightFrame(altitude < threshold ? 'fixed' : 'inertial');
  }

  private checkViewBody(): void {
    // Tour mode assigns `freeFlightBody` itself at every transit — the
    // sphere-of-influence handover below is for manual free flight only, and
    // could otherwise relabel the camera mid-orbit around a small moon whose
    // SOI the standoff radius happens to exceed.
    if (this.tourActive) return;
    if (!this.freeFlight.active || !this.freeFlightBody) return;
    // The sphere-of-influence test and the body handover are both defined in
    // inertial terms, so drop back to that frame first; the mode is re-chosen
    // immediately afterwards against the new body.
    this.setFreeFlightFrame('inertial');
    const current = this.freeFlightBody;
    const rootPos = this.system.frames.transformPosition(
      this.freeFlight.position,
      current.inertialFrameId,
      this.system.root,
      this.time,
    );
    const containing = this.system.bodyContaining(rootPos, this.time) ?? this.system.star;
    if (containing.id === current.id) return;

    this.freeFlight.position.copy(
      this.system.frames.transformPosition(
        this.freeFlight.position,
        current.inertialFrameId,
        containing.inertialFrameId,
        this.time,
      ),
    );
    this.freeFlightBody = containing;
    if (containing.def.terrain) {
      this.setActiveBody(containing);
    }
  }

  /**
   * Keeps the free-flight camera from clipping through solid ground.
   *
   * Queries the same `HeightField` the terrain mesh and landing gear use, so
   * the floor here is the actual ground under the camera — mountains
   * included — not the sea-level (or dry-body datum) sphere this used to
   * fall back to, which let the camera sink into anything above datum and,
   * on an ocean-less body, gave no floor to reach at all. `FREE_FLIGHT_
   * GROUND_MARGIN` keeps the near plane from clipping into the terrain it
   * was just stopped on top of.
   *
   * This is one `HeightField` query per frame against whichever body the
   * camera is near — the same per-step cost the vessel and character already
   * pay for their own ground contact, and `HeightField` itself caches
   * samples, so a hovering or slowly-moving camera re-queries almost
   * nothing.
   */
  private clampFreeFlightAboveGround(): void {
    if (!this.freeFlight.active || !this.freeFlightBody) return;
    const body = this.freeFlightBody;
    const surface = this.surfaces.get(body.id);

    // `freeFlight.position` is always expressed relative to `freeFlightBody`
    // itself — in its fixed frame or its (body-centred) inertial frame,
    // whichever `freeFlightFrame` currently is — so its own length is
    // already the distance from the body's centre; see `cameraAltitude`'s
    // doc comment for the same assumption. Terrain, however, is fixed to the
    // body's rotation, so the height-field query needs the fixed-frame
    // direction specifically, regardless of which frame the camera is in.
    const distance = this.freeFlight.position.length();
    if (distance <= 1e-3) return;

    let floorRadius = body.radius;
    if (surface) {
      const positionFixed =
        this.freeFlightFrame === 'fixed'
          ? this.freeFlight.position
          : this.system.frames.transformPosition(
              this.freeFlight.position,
              body.inertialFrameId,
              body.fixedFrameId,
              this.time,
            );
      floorRadius = surface.heightField.surfaceRadiusAt(positionFixed);
    }
    floorRadius += Game.FREE_FLIGHT_GROUND_MARGIN;
    if (!Number.isFinite(floorRadius) || floorRadius <= 0) return;

    if (distance < floorRadius) {
      this.freeFlight.position.mulScalar(floorRadius / distance);
    }
  }

  private updateStreaming(): void {
    this.checkViewBody();
    this.updateFreeFlightFrame();
    this.clampFreeFlightAboveGround();
    this.updateActiveBodySet();

    const viewBody = this.viewBody;
    const cameraPosition = this.cameraPositionInFrame();

    for (const id of this.activeBodyIds) {
      const surface = this.surfaces.get(id);
      if (!surface) continue;

      const positionFixed = this.system.frames.transformPosition(
        cameraPosition,
        viewBody.inertialFrameId,
        surface.body.fixedFrameId,
        this.time,
      );
      // Only the view body's own velocity biases streaming priority toward
      // where the camera is heading — a body the camera merely happens to be
      // near has no such intent of approach to predict.
      const velocityFixed =
        id === viewBody.id
          ? this.system.frames.transformState(
              cameraPosition,
              this.ship.body.velocity,
              viewBody.inertialFrameId,
              surface.body.fixedFrameId,
              this.time,
            ).velocity
          : new Vec3();

      surface.update(positionFixed, velocityFixed, this.time);
      this.terrainRenderers.get(id)?.setVisible(surface.visible);
      this.vegetation.get(id)?.setVisible(surface.visible);
    }

    // Floating origin: rebase when the camera drifts, and rebase everything that
    // caches a local position.
    this.floatingOrigin.update(cameraPosition);
  }

  /** Camera position in the reference body's inertial frame. */
  private cameraPositionInFrame(out = new Vec3()): Vec3 {
    if (this.freeFlight.active) {
      // In surface mode the camera's state is held in the body's rotating
      // frame; everything downstream works in inertial axes, so convert here
      // rather than making every caller aware of the mode.
      if (this.freeFlightFrame === 'fixed' && this.freeFlightBody) {
        return out.copy(
          this.system.frames.transformPosition(
            this.freeFlight.position,
            this.freeFlightBody.fixedFrameId,
            this.freeFlightBody.inertialFrameId,
            this.time,
          ),
        );
      }
      return out.copy(this.freeFlight.position);
    }

    if (this.onFoot && this.activeSurface) {
      const eyeFixed = this.character.eyePosition();
      const inInertial = this.system.frames.transformPosition(
        eyeFixed,
        this.ship.referenceBody.fixedFrameId,
        this.ship.referenceBody.inertialFrameId,
        this.time,
      );
      return out.copy(inInertial);
    }

    if (this.cameraMode === 'chase') {
      // Behind and above the vessel: -Z is aft, since +Z is the nose.
      const back = this.ship.body.orientation.rotate(new Vec3(0, 2.4, -this.ship.def.size[2] * 1.4));
      return Vec3.addVectors(this.ship.body.position, back, out);
    }
    // Cockpit: slightly forward and above the centre of mass.
    const offset = this.ship.body.orientation.rotate(
      new Vec3(0, this.ship.def.size[1] * 0.28, this.ship.def.size[2] * 0.22),
    );
    return Vec3.addVectors(this.ship.body.position, offset, out);
  }

  /**
   * Altitude of the *camera* above the reference body's surface, metres.
   *
   * This is not the vessel's altitude, and the difference is the whole reason
   * this method exists. Nearly everything the renderer decides per frame — which
   * depth partitions to use, how dense the air is where the viewer sits, how
   * much skylight washes out the stars, whether the ocean is drawn as a local
   * tangent patch or as a whole sphere — is a property of where the *viewer* is.
   * Feeding the published navigation altitude into those was correct only while
   * the camera was rigidly attached to the ship: in free flight the camera
   * detaches and can sit four planetary radii out while the vessel is still
   * parked on the ground, and every one of those decisions was then made for the
   * ship's location. The visible symptoms were an atmosphere that appeared only
   * once you actually landed, and a ninety-kilometre flat ocean patch oriented
   * beneath the ship being viewed from orbit.
   */
  private cameraAltitude(cameraPosition: Vec3): number {
    const body = this.viewBody;
    const surface = this.activeSurface;
    if (!surface) return cameraPosition.length() - body.radius;
    const fixed = this.system.frames.transformPosition(
      cameraPosition,
      body.inertialFrameId,
      body.fixedFrameId,
      this.time,
    );
    return surface.heightField.altitudeAt(fixed);
  }

  /**
   * Camera orientation in the reference body's inertial frame.
   *
   * Both the vessel and the on-foot view define +Z as "forward", but a Three.js
   * camera looks down its own -Z. The half turn about +Y converts between the
   * two: without it the cockpit camera faces out of the vessel's tail.
   */
  /**
   * Where the player is looking, before the renderer's view convention is
   * applied. Three cameras look down -Z and the vessel's nose is +Z, so
   * `cameraOrientation` adds a half turn that anything reasoning about facing
   * must not inherit.
   */
  private shipFacing(): Quat {
    if (this.freeFlight.active) return this.freeFlight.orientation.clone();
    return this.onFoot
      ? this.system.frames.transformOrientation(
          this.character.viewOrientation(),
          this.ship.referenceBody.fixedFrameId,
          this.ship.referenceBody.inertialFrameId,
          this.time,
        )
      : this.ship.body.orientation.clone();
  }

  private cameraOrientation(): Quat {
    if (this.freeFlight.active) {
      const facing =
        this.freeFlightFrame === 'fixed' && this.freeFlightBody
          ? this.system.frames.transformOrientation(
              this.freeFlight.orientation,
              this.freeFlightBody.fixedFrameId,
              this.freeFlightBody.inertialFrameId,
              this.time,
            )
          : this.freeFlight.orientation.clone();
      return facing.multiply(VIEW_FLIP);
    }

    const facing = this.onFoot
      ? this.system.frames.transformOrientation(
          this.character.viewOrientation(),
          this.ship.referenceBody.fixedFrameId,
          this.ship.referenceBody.inertialFrameId,
          this.time,
        )
      : this.ship.body.orientation.clone().multiply(this.freeLookOffset());
    return facing.multiply(VIEW_FLIP);
  }

  /**
   * Camera-only look-around offset, applied in the ship's own body frame
   * before the view-convention flip. Held via the `freeLook` input, it never
   * touches `mouseStick`, so panning the view cannot itself command a
   * rotation the flight computer would then have to fight or damp out.
   */
  private freeLookOffset(): Quat {
    const yaw = this.input.axes.freeLookYaw;
    const pitch = this.input.axes.freeLookPitch;
    if (yaw === 0 && pitch === 0) return Quat.identity();
    // Signs mirror the rotation-stick convention: +Y yaws the nose left, +X
    // pitches it down, so looking right/up needs the negated angle.
    return Quat.fromAxisAngle(new Vec3(0, 1, 0), -yaw).multiply(Quat.fromAxisAngle(new Vec3(1, 0, 0), -pitch));
  }

  /**
   * Predicted-orbit line and apsis/impact markers.
   *
   * Reuses `TrajectoryPredictor` — the same closed-form propagation the
   * manoeuvre display and analytical time warp already rely on — so the line
   * drawn here cannot disagree with what the HUD's predicted-events list or a
   * warp jump would actually do. Only meaningful while the vessel's own
   * reference body is the one on screen: hidden on foot, mid-tour, once
   * landed, and while free-flying past a different body's sphere of
   * influence (the ship's position/velocity are in a frame that no longer
   * matches `cameraPosition` there).
   *
   * Gated on "looking around" on top of that — held `freeLook` in the
   * cockpit/chase camera, or free flight itself (which has no ship rotation
   * to protect, so it doesn't need the key) — since these are world-space
   * lines, not a HUD plane, and leaving them on permanently would clutter
   * whatever the nose happens to be pointed at.
   */
  private updateTrajectoryOverlay(body: CelestialBody, cameraPosition: Vec3): void {
    const lookingAround = this.freeFlight.active || this.input.isDown('freeLook');
    const show =
      lookingAround &&
      !this.onFoot &&
      !this.tourActive &&
      !this.ship.contact.landed &&
      this.ship.referenceBody.id === body.id;

    if (!show) {
      this.trajectoryLine.setPath([], 0);
      this.apsisMarkers.set([]);
      return;
    }

    const prediction = this.predictor.predict(body, this.ship.body.position, this.ship.body.velocity, this.time, {
      samples: 128,
      terrainCeiling: this.surfaces.get(body.id)?.heightField.ceiling ?? 0,
      detectSoiTransitions: false,
    });

    const toRenderSpace = (p: Vec3): THREE.Vector3 => {
      const offset = Vec3.subVectors(p, cameraPosition);
      return new THREE.Vector3(offset.x, offset.y, offset.z);
    };

    const color = prediction.impacts ? 0xff6b57 : prediction.escape ? 0xffcf5c : 0x7fd1ff;
    this.trajectoryLine.setPath(prediction.points.map(toRenderSpace), color);

    const markerColors: Partial<Record<TrajectoryEvent['kind'], number>> = {
      periapsis: 0x53d971,
      apoapsis: 0x5ab0ff,
      impact: 0xff4d4d,
      atmosphereEntry: 0xffa64d,
    };
    const markers = prediction.events
      .filter((e) => markerColors[e.kind] !== undefined)
      .map((e) => ({ position: toRenderSpace(e.position), color: markerColors[e.kind]! }));
    this.apsisMarkers.set(markers);
  }

  /**
   * Draws the transfer macro's planned route: every arc of the patched-conic
   * chain, not just the conic the vessel is currently on.
   *
   * Points are cached in the **root** frame rather than in the view body's,
   * and each point is converted using its own time. That combination is what
   * makes a multi-arc route drawable at all: an arc about a moon and an arc
   * about the star are expressed in frames that are moving relative to each
   * other, so the only place they agree on where a point physically is, is the
   * system inertial frame at the moment the vessel is there. The cache is
   * rebuilt only when the route object changes, because the transform does not
   * depend on the current time — only the camera subtraction does, and that is
   * per-frame and cheap.
   *
   * Visible whenever the macro is running (the route is the thing the player
   * most needs to see then) and, when idle, behind the same "looking around"
   * gate as the trajectory line — see `updateTrajectoryOverlay`.
   */
  private updateRouteOverlay(body: CelestialBody, cameraPosition: Vec3): void {
    const macro = this.macros.current;
    const transfer = macro instanceof TransferToTargetMacro ? macro : null;
    const lookingAround = this.freeFlight.active || this.input.isDown('freeLook');

    let route = transfer?.route ?? null;
    if (!route && lookingAround && !this.onFoot && !this.tourActive && this.targetBodyId) {
      route = this.idleRoute(body);
    }

    if (!route || this.onFoot || this.tourActive) {
      this.hideRouteOverlay();
      return;
    }

    this.routeOverlay.show(
      route,
      this.system.frames.transformPosition(cameraPosition, body.inertialFrameId, this.system.root, this.time),
    );
  }

  private hideRouteOverlay(): void {
    this.routeOverlay.hide();
  }

  /**
   * Coast-only route from the vessel's current state — where it is actually
   * headed, with no burn assumed. Recomputed on a timer rather than per frame:
   * a chained prediction is far too expensive to run every frame, and the
   * answer barely moves between recomputes.
   */
  private idleRoute(body: CelestialBody): ChainedPrediction | null {
    if (this.ship.referenceBody.id !== body.id || this.ship.contact.landed) return null;
    if (this.time - this.idleRouteTime < IDLE_ROUTE_INTERVAL && this.idleRouteCache) {
      return this.idleRouteCache;
    }
    this.idleRouteTime = this.time;
    this.idleRouteCache = this.predictor.predictChained(
      body,
      this.ship.body.position,
      this.ship.body.velocity,
      this.time,
      {
        samples: 96,
        maxArcs: 4,
        arriveAtBodyId: this.targetBodyId ?? undefined,
        terrainCeilingFor: (b) => this.surfaces.get(b.id)?.heightField.ceiling ?? 0,
      },
    );
    return this.idleRouteCache;
  }

  /**
   * Marks the selected navigation target: a dot at its live position when
   * it's in view, an edge arrow pointing toward it when it isn't.
   *
   * Position is recomputed fresh every frame rather than read from
   * `computer.targetPosition` — that field is a snapshot taken once when
   * `setTarget` is called (see its use in the manoeuvre/docking holds) and
   * goes stale as the target body moves, which a marker meant to show where
   * the target *is* cannot afford.
   *
   * Gated on "looking around" for the same reason as the trajectory line:
   * both are world-space, not a HUD plane, and only worth drawing while the
   * pilot is deliberately checking rather than permanently in view.
   *
   * On foot there is nothing to gate on, so there is no gate. The mouse
   * already drives the walker's own view (`Character.viewOrientation`, fed
   * through `cameraOrientation`); `freeLook` is a cockpit-only modifier that
   * the on-foot camera path never consults, and there is no vessel attitude
   * a look-around could disturb. The player is always looking through their
   * own eyes, so the indicators show whenever a target is set. Suppressing
   * them outright on foot — which is what this did — is why the navigation
   * target was invisible for the whole time the player was walking.
   */
  private updateTargetMarker(body: CelestialBody, cameraPosition: Vec3): void {
    const lookingAround = this.onFoot || this.freeFlight.active || this.input.isDown('freeLook');
    if (!lookingAround || !this.targetBodyId) {
      this.targetMarker.set([]);
      this.targetArrow.hide();
      return;
    }
    const targetBody = this.system.find(this.targetBodyId);
    if (!targetBody) {
      this.targetMarker.set([]);
      this.targetArrow.hide();
      return;
    }
    const targetPosition = this.system.frames.transformPosition(
      new Vec3(),
      targetBody.inertialFrameId,
      body.inertialFrameId,
      this.time,
    );
    const offset = Vec3.subVectors(targetPosition, cameraPosition);
    const distance = offset.length();
    if (distance < 1e-3) {
      this.targetMarker.set([]);
      this.targetArrow.hide();
      return;
    }
    const dir = offset.clone().mulScalar(1 / distance);

    // The view orientation already IS the camera's actual basis (see
    // `render`'s `this.viewOrientation.copy(orientation)`), so the target's
    // bearing is a plain change of basis — no need to touch a THREE camera's
    // projection matrix to know whether it's in frame.
    const forward = this.viewOrientation.rotate(new Vec3(0, 0, -1));
    const right = this.viewOrientation.rotate(new Vec3(1, 0, 0));
    const up = this.viewOrientation.rotate(new Vec3(0, 1, 0));
    const compX = dir.dot(right);
    const compY = dir.dot(up);
    const compZ = dir.dot(forward);

    const halfFovY = (this.renderer.fov * Math.PI) / 360;
    const halfFovX = Math.atan(Math.tan(halfFovY) * this.renderer.worldMidCamera.aspect);
    const margin = 0.92;
    const onScreen =
      compZ > 1e-4 &&
      Math.abs(compX / compZ) <= Math.tan(halfFovX) * margin &&
      Math.abs(compY / compZ) <= Math.tan(halfFovY) * margin;

    if (onScreen) {
      this.targetMarker.set([{ position: new THREE.Vector3(offset.x, offset.y, offset.z), color: 0xffd166 }]);
      this.targetArrow.hide();
      return;
    }

    this.targetMarker.set([]);
    const theta = Math.atan2(compY, compX);
    const edgeX = Math.cos(theta) * Math.tan(halfFovX) * margin;
    const edgeY = Math.sin(theta) * Math.tan(halfFovY) * margin;
    const clampedDir = right
      .clone()
      .mulScalar(edgeX)
      .addScaled(up, edgeY)
      .addScaled(forward, 1)
      .normalize();
    const arrowPos = clampedDir.mulScalar(this.targetArrowDistance);
    this.targetArrow.show(new THREE.Vector3(arrowPos.x, arrowPos.y, arrowPos.z), theta - Math.PI / 2);
  }

  /**
   * Advances every body's cross-fade toward its target and pushes the result at
   * the layers that draw it.
   *
   * Driven by REAL time, not simulation time: this is a presentation
   * transition, and at 10000x warp a simulation-time ramp would complete inside
   * a single frame, which is the pop it exists to remove.
   *
   * A body fades out as well as in. That only works because the prune in
   * `updateActiveBodySet` is told to keep anything still mid-fade — otherwise
   * demotion disposes the terrain on the same frame and there is nothing left
   * to dissolve.
   */
  private updateSurfaceFades(realDelta: number): void {
    const step = realDelta / SURFACE_FADE_SECONDS;
    for (const id of this.activeBodyIds) {
      if (!this.surfaceFades.has(id)) this.surfaceFades.set(id, 0);
    }
    for (const id of this.activeCloudOnlyIds) {
      if (!this.surfaceFades.has(id)) this.surfaceFades.set(id, 0);
    }

    for (const [id, current] of [...this.surfaceFades]) {
      // A terrain body only starts fading in once its surface has actually
      // streamed: dissolving the impostor away over a half-built planet would
      // reveal holes rather than ground.
      const wantsIn = this.readyBodyIds.has(id) || this.desiredCloudOnlyIds.has(id);
      const target = wantsIn ? 1 : 0;
      const next =
        target > current ? Math.min(target, current + step) : Math.max(target, current - step);
      if (next <= 0 && !wantsIn) this.surfaceFades.delete(id);
      else this.surfaceFades.set(id, next);

      this.terrainRenderers.get(id)?.setSurfaceFade(next);
      this.oceans.get(id)?.setSurfaceFade(next);
      this.clouds.get(id)?.setSurfaceFade(next);
    }
  }

  /** Bodies still dissolving in or out, whose renderers must not be disposed yet. */
  private fadingBodyIds(): Set<string> {
    const ids = new Set<string>();
    for (const [id, fade] of this.surfaceFades) {
      if (fade > 0 && fade < 1) ids.add(id);
    }
    return ids;
  }

  /**
   * How one body is lit as seen from one viewpoint.
   *
   * These terms used to be re-derived at three separate call sites — the
   * viewer's own body inline in `render`, each promoted neighbour in
   * `updateBodyVisuals`, and each cloud-only gas giant in
   * `updateCloudOnlyVisuals`. They were the same arithmetic three times over,
   * which is three places to re-tune a constant and two places to forget.
   * There is one now; the differences that are genuinely per-viewpoint stay as
   * arguments.
   *
   * `shine` names the scratch direction and colour that `planetshineAt` should
   * write into, or is null for a viewpoint with nothing to catch planetshine
   * (a bare cloud deck) — which also skips computing it.
   */
  private bodyLighting(
    body: CelestialBody,
    cameraPositionInBody: Vec3,
    altitude: number,
    t: number,
    shine: { direction: Vec3; color: THREE.Color } | null,
  ): BodyLighting {
    const sunDirection = this.sunDirectionAt(body, cameraPositionInBody, t);
    const irradiance = this.irradianceAt(body, cameraPositionInBody, t);
    const atmosphere = this.atmospheres.get(body.id);
    const density = atmosphere ? atmosphere.densityAt(altitude) : 0;
    const sunDotUp = sunDirection.dot(cameraPositionInBody.normalized());
    // The camera-local terminator. The terrain shader evaluates the same ramp
    // per fragment (see SURFACE_DAYLIGHT_START); this one number describes a
    // whole planet's terminator, which is only a good description of it while
    // the observer is standing on that planet. So it is used only for terms
    // that are genuinely camera-local — the display gain below, the shared
    // directional light, the vegetation billboards.
    const daylight = smoothstep(SURFACE_DAYLIGHT_START, SURFACE_DAYLIGHT_END, sunDotUp);
    const planetshine = shine
      ? this.planetshineAt(body, cameraPositionInBody, t, shine.direction, shine.color)
      : 0;
    return {
      sunDirection,
      irradiance,
      // Light delivered to the shaders, normalised against Halcyon's
      // insolation. The clamp is narrow on purpose — see `render`, which
      // deliberately feeds the exposure this same clamped value.
      sunIntensity: clamp(irradiance / 1361, 0.25, 2.5) * 3.2,
      altitude,
      density,
      skyStrength: clamp01(density * 0.9) * clamp01(sunDotUp + 0.35),
      daylight,
      planetshine,
      // Scaled into the range the display can actually show, and gated on
      // darkness so it contributes nothing at noon. See `render`.
      planetshineForDisplay: planetshine * PLANETSHINE_DISPLAY_GAIN * (1 - daylight),
    };
  }

  /**
   * Ambient, planetshine, cloud shadow, eclipse and self-shadow for one body's
   * terrain. Shared by the viewer's own body and by every promoted neighbour —
   * the two used to carry identical copies of this block.
   *
   * `terrain.update` is deliberately NOT called here: the viewer's body has to
   * be positioned before the shadow cascades render (see `render`), which is
   * earlier than this runs.
   */
  private applyTerrainLighting(
    body: CelestialBody,
    terrain: TerrainRenderer,
    lighting: BodyLighting,
    clouds: CloudLayer | null,
    shineDirection: Vec3,
    shineColor: THREE.Color,
    t: number,
  ): void {
    terrain.setLighting(lighting.sunDirection, this.starColor, lighting.sunIntensity, lighting.altitude);

    // Ambient scales with atmospheric density: a vacuum has no skylight, so a
    // shadow on an airless moon really is black. The sky can stay luminous
    // after the star drops below the geometric horizon, but only a fraction of
    // that reaches the ground — that attenuation is applied per fragment in
    // the terrain shader (SURFACE_NIGHT_SKY_FRACTION) rather than folded in
    // here, because from orbit one camera-derived daylight factor would dim
    // the skylight on the whole planet at once.
    const haze = body.atmosphere.hazeColor;
    terrain.setAmbient(
      this.scratchSkyAmbient
        .setRGB(haze[0], haze[1], haze[2])
        .multiplyScalar(lighting.skyStrength * 0.28 * lighting.sunIntensity),
      this.scratchGroundAmbient.setRGB(0.02, 0.02, 0.024).multiplyScalar(0.4 + lighting.skyStrength),
    );
    terrain.setPlanetshine(shineDirection, shineColor, lighting.planetshineForDisplay);

    // Cloud shadows. The sun direction has to be converted into planet-fixed
    // axes because that is the frame the cloud field is defined over — the
    // deck sits above the ground, not above a fixed point in space.
    const sunFixed = this.system.frames.transformDirection(
      lighting.sunDirection,
      body.inertialFrameId,
      body.fixedFrameId,
      t,
    );
    const shadow = clouds?.shadowParams;
    terrain.setCloudShadow(
      sunFixed,
      this.debugLayers.clouds ? (shadow?.deckRadius ?? 0) : 0,
      shadow?.coverage ?? 0,
      shadow?.drift ?? 0,
      shadow?.banded ?? 0,
      t,
    );

    const eclipse = this.eclipseShadowsEnabled ? this.eclipseOccluderAt(body, t) : null;
    terrain.setEclipseShadow(eclipse?.centreFixed ?? Vec3.zero(), eclipse?.radius ?? 0);
    terrain.setSelfShadow(this.renderer.getShadowCascades());
    terrain.setShadowFade(this.renderer.shadowFade);
  }

  /**
   * Cloud deck and ocean surface for one body. Shared by all three paths: the
   * viewer's own body, a promoted neighbour, and a cloud-only gas giant (which
   * simply has no ocean).
   */
  private applyCloudAndOceanVisuals(
    lighting: BodyLighting,
    clouds: CloudLayer | null,
    ocean: OceanRenderer | null,
    cameraPositionInBody: Vec3,
    planetCentreRender: Vec3,
    fixedToInertial: Quat,
    t: number,
  ): void {
    if (clouds) {
      clouds.update(planetCentreRender, fixedToInertial, t);
      clouds.setLighting(lighting.sunDirection, this.starColor, lighting.sunIntensity, lighting.altitude);
    }

    if (ocean) {
      ocean.update(cameraPositionInBody, planetCentreRender, lighting.altitude, t, fixedToInertial);
      ocean.setLighting(lighting.sunDirection, this.starColor, lighting.sunIntensity);
      ocean.setSelfShadow(this.renderer.getShadowCascades());
      ocean.setShadowFade(this.renderer.shadowFade);
    }
  }

  /**
   * Everything visual for one promoted body that is *not* the one the camera
   * is on — lit from its own position, because a moon's sun angle is not its
   * planet's.
   *
   * `cameraPositionInBody` is the camera's position relative to `body`'s
   * centre, in that body's inertial axes — equivalently, since inertial frames
   * only translate relative to the root and never rotate, in root-aligned
   * axes.
   */
  private updateBodyVisuals(
    body: CelestialBody,
    terrain: TerrainRenderer,
    cameraPositionInBody: Vec3,
    t: number,
  ): void {
    const cameraFixed = this.system.frames.transformPosition(
      cameraPositionInBody,
      body.inertialFrameId,
      body.fixedFrameId,
      t,
    );
    const fixedToInertial = this.system.frames.stateInRoot(body.fixedFrameId, t).orientation;
    const planetCentreRender = cameraPositionInBody.clone().negate();

    terrain.update(cameraFixed, fixedToInertial, planetCentreRender);

    const surface = this.surfaces.get(body.id);
    const altitude = surface
      ? surface.heightField.altitudeAt(cameraFixed)
      : Math.max(0, cameraPositionInBody.length() - body.radius);
    const lighting = this.bodyLighting(body, cameraPositionInBody, altitude, t, this.secondaryShine);
    const clouds = this.clouds.get(body.id) ?? null;

    this.applyTerrainLighting(
      body,
      terrain,
      lighting,
      clouds,
      this.secondaryPlanetshineDirection,
      this.secondaryPlanetshineColor,
      t,
    );
    this.applyCloudAndOceanVisuals(
      lighting,
      clouds,
      this.oceans.get(body.id) ?? null,
      cameraPositionInBody,
      planetCentreRender,
      fixedToInertial,
      t,
    );
  }

  /**
   * `updateBodyVisuals`'s counterpart for a gas giant's cloud deck: no
   * `TerrainRenderer` exists for one of these, so everything that assumes one
   * (ambient, shadows, eclipse) is simply absent. See `activeCloudOnlyIds`.
   */
  private updateCloudOnlyVisuals(
    body: CelestialBody,
    cameraPositionInBody: Vec3,
    t: number,
  ): void {
    const clouds = this.clouds.get(body.id);
    if (!clouds) return;
    const fixedToInertial = this.system.frames.stateInRoot(body.fixedFrameId, t).orientation;
    const planetCentreRender = cameraPositionInBody.clone().negate();
    const altitude = Math.max(0, cameraPositionInBody.length() - body.radius);
    // No planetshine: a bare cloud deck has no terrain term to fill.
    const lighting = this.bodyLighting(body, cameraPositionInBody, altitude, t, null);
    this.applyCloudAndOceanVisuals(
      lighting,
      clouds,
      null,
      cameraPositionInBody,
      planetCentreRender,
      fixedToInertial,
      t,
    );
  }

  /**
   * Builds the render state and draws.
   *
   * COORDINATE POLICY, and it is worth being explicit because getting it wrong
   * is invisible until nothing appears on screen:
   *
   *   - Every renderable object is positioned at `worldPosition - cameraPosition`,
   *     computed in 64-bit and narrowed to 32-bit only at the very end. Those
   *     offsets are small near the camera, which is where precision matters.
   *   - Those offsets stay in the reference body's INERTIAL axes. They are not
   *     pre-rotated into the camera's frame.
   *   - The camera sits at the origin carrying the real orientation, and
   *     Three.js applies the view rotation exactly once.
   *
   * Rotating the world into the camera frame *and* giving the camera an
   * orientation applies the rotation twice, which silently throws the entire
   * scene off-screen while still reporting healthy draw calls.
   *
   * All inertial frames in the system share their axes with the root frame
   * (orbital motion translates a frame, it does not rotate it), so directions
   * computed in the root frame need no conversion here.
   */
  private render(): void {
    // The viewer's body, not the vessel's: everything below describes the world
    // as seen from the camera. See viewBody.
    const body = this.viewBody;
    const t = this.time;
    const cameraPosition = this.cameraPositionInFrame();
    const orientation = this.cameraOrientation();
    this.viewOrientation.copy(orientation);
    this.renderer.setViewOrientation(orientation);

    // --- Lighting -----------------------------------------------------------
    // The planet's centre, relative to the camera, in inertial axes.
    const planetCentreRender = this.scratchA.copy(cameraPosition).negate();
    // The viewer's altitude, not the vessel's: see cameraAltitude. Everything
    // below this line describes the world as seen from the camera, so in free
    // flight it has to follow the camera.
    const altitude = Math.max(0, this.cameraAltitude(cameraPosition));

    this.renderer.adaptDepthRanges(
      altitude,
      body.radius,
      cameraPosition.length(),
      body.hasAtmosphere ? body.radius + body.atmosphereTop : 0,
    );
    this.renderer.adaptShadowDistance(altitude);

    // Same derivation every promoted body gets — see `bodyLighting`. The one
    // thing special about the viewer's own body is the altitude fed to it:
    // `cameraAltitude` follows the camera, which in free flight is not the
    // vessel and not a heightfield lookup either.
    //
    // Two of these terms carry reasoning worth keeping in view here, because
    // the exposure block at the bottom of this method depends on both:
    //
    //   `sunIntensity` is clamped narrowly on purpose, and the exposure
    //   deliberately reads that SAME clamped value. Widening the clamp for the
    //   exposure alone looks like the obvious way to make dim worlds legible
    //   and it inverts the system: on-screen brightness goes as
    //   I_shader / I_exposure^0.8, so feeding the exposure the true 400x range
    //   while the shaders keep a clamped 10x one leaves Kolyma twelve times
    //   brighter than Vesper — the dim world outshining the furnace. Sharing
    //   the clamp gives I^0.2 instead, a 1.6x spread in the right order.
    //   (Kolyma reading almost black was never this clamp; it was the missing
    //   output encoding in the terrain shader. See TerrainMaterial.)
    //
    //   `planetshineForDisplay` is scaled far above the physical figure.
    //   Aegis lights Ferrum with ~1.5% of Ferrum's own daylight — far brighter
    //   than earthshine's 0.01%, and still four orders of magnitude below what
    //   an exposure calibrated for the day side can render. What closes that
    //   gap in reality is the eye, which spans five or six orders of magnitude;
    //   a display spans two. So the night fill is scaled instead — the same
    //   trade every renderer makes, better stated than hidden.
    const lighting = this.bodyLighting(body, cameraPosition, altitude, t, this.primaryShine);
    const { sunDirection, irradiance, sunIntensity, density, skyStrength, daylight } = lighting;
    const planetshineForDisplay = lighting.planetshineForDisplay;

    // Standard materials do not know that the planet blocks a directional
    // light. In particular, DoubleSide vegetation can flip an underside
    // normal toward a star that is physically below the local horizon and
    // appear floodlit at midnight. Gate the shared star light with the same
    // smooth terminator the terrain uses. Camera-local is right for these two:
    // the directional light and the vegetation billboards only ever light
    // things within a few hundred metres of the viewer.
    const localSunIntensity = sunIntensity * daylight;

    // Terrain chunks have to be at THIS frame's camera-relative position
    // before the shadow cascades render, not last frame's. `renderShadowCascades`
    // renders whatever is currently in `worldScene`, and chunk meshes are
    // positioned camera-relative (see TerrainRenderer.update) — repositioning
    // them only after the cascades had already rendered meant every cascade
    // depth map was one frame stale relative to the camera actually in use
    // this frame, which reads as the shadow swimming against the ground it
    // belongs to rather than sitting still on it.
    let fixedToInertial: Quat | null = null;
    let cameraFixed: Vec3 | null = null;
    if (this.activeTerrain) {
      cameraFixed = this.system.frames.transformPosition(
        cameraPosition,
        body.inertialFrameId,
        body.fixedFrameId,
        t,
      );
      // Rotation from planet-fixed axes into inertial axes. Chunk geometry is
      // authored in planet-fixed coordinates, so this is what makes the terrain
      // turn with the planet.
      fixedToInertial = this.system.frames.stateInRoot(body.fixedFrameId, t).orientation;
      this.activeTerrain.update(cameraFixed, fixedToInertial, planetCentreRender);
    }

    // Star light for standard-material objects (the vessel hull), moved
    // ahead of the terrain-lighting blocks below rather than after them: the
    // self-shadow cascades derive their direction from `starLight.position`,
    // and the terrain blocks read the cascades back via `getShadowCascades`,
    // so this frame's direction has to land before either of them runs.
    this.renderer.setStarLight(
      this.scratchStarLightDirection.set(sunDirection.x, sunDirection.y, sunDirection.z),
      this.starColor,
      localSunIntensity,
      skyStrength,
    );
    this.renderer.updateShadowCascades(cameraPosition);

    // Solid-tier vegetation (trees, rocks, crystals — see VegetationLayer's
    // terrainShadowCaster layer) reads the same cascades terrain and ocean
    // do, through one shared material's uniforms rather than per-chunk
    // pushes: there is exactly one draw call's worth of state to keep
    // current per frame, not one per chunk.
    const vegShadowUniforms = getVegetationShadowUniforms();
    setShadowCascadeUniforms(vegShadowUniforms, this.renderer.getShadowCascades());
    setShadowFadeUniform(vegShadowUniforms, this.renderer.shadowFade);
    vegShadowUniforms.uSunDirectionWorld.value.copy(this.renderer.starLight.position).normalize();

    if (this.activeTerrain && fixedToInertial) {
      this.applyTerrainLighting(
        body,
        this.activeTerrain,
        lighting,
        this.activeClouds,
        this.planetshineDirection,
        this.planetshineColor,
        t,
      );
      this.applyCloudAndOceanVisuals(
        lighting,
        this.activeClouds,
        this.activeOcean,
        cameraPosition,
        planetCentreRender,
        fixedToInertial,
        t,
      );

      // Vegetation is the one layer only the viewer's own body ever has: it is
      // scattered within a few hundred metres of the camera, so a promoted
      // neighbour never carries any.
      if (this.activeVegetation && cameraFixed) {
        this.activeVegetation.update(cameraFixed, fixedToInertial, t, altitude);
        this.activeVegetation.setLighting(this.starColor, localSunIntensity);
      }
    }

    // Other bodies in the view body's local group (a planet's moons, or a
    // moon's parent and siblings) close enough to also be promoted — see
    // `updateActiveBodySet`. Each is lit from its own position, not the
    // viewer's: a moon's sun angle is not its planet's.
    for (const id of this.activeBodyIds) {
      if (id === body.id) continue;
      const otherBody = this.system.find(id);
      const otherTerrain = this.terrainRenderers.get(id);
      if (!otherBody || !otherTerrain) continue;
      const cameraInOther = this.system.frames.transformPosition(
        cameraPosition,
        body.inertialFrameId,
        otherBody.inertialFrameId,
        t,
      );
      this.updateBodyVisuals(otherBody, otherTerrain, cameraInOther, t);
    }

    // Gas giants in the promoted set, cloud deck only — see
    // `activeCloudOnlyIds`.
    for (const id of this.activeCloudOnlyIds) {
      const otherBody = this.system.find(id);
      if (!otherBody) continue;
      const cameraInOther = this.system.frames.transformPosition(
        cameraPosition,
        body.inertialFrameId,
        otherBody.inertialFrameId,
        t,
      );
      this.updateCloudOnlyVisuals(otherBody, cameraInOther, t);
    }

    this.renderer.setPlanetshine(this.planetshineColor, lighting.planetshine);

    // --- Vessel -------------------------------------------------------------
    // The vessel's position lives in ITS reference body's frame, which is not
    // necessarily the frame everything else here is in. Converting is a no-op
    // in the usual case and the difference between a visible ship and one
    // drawn an orbit away in free flight.
    const shipInViewFrame =
      this.ship.referenceBody.id === body.id
        ? this.ship.body.position
        : this.system.frames.transformPosition(
            this.ship.body.position,
            this.ship.referenceBody.inertialFrameId,
            body.inertialFrameId,
            t,
          );
    const shipOffset = Vec3.subVectors(shipInViewFrame, cameraPosition);
    this.shipModel.root.position.set(shipOffset.x, shipOffset.y, shipOffset.z);
    const q = this.ship.body.orientation;
    this.shipModel.root.quaternion.set(q.x, q.y, q.z, q.w);
    // Free flight detaches the camera from whatever `cameraMode` it was left
    // in (there is no 'freeFlight' camera mode — see its declaration), so a
    // vessel-in-cockpit-view player who toggles free flight without also
    // toggling `cameraMode` would otherwise keep the ship hidden even though
    // the free camera can now see it from outside.
    this.shipModel.root.visible = this.freeFlight.active || !(this.cameraMode === 'cockpit' && !this.onFoot);

    // --- On-foot avatar -------------------------------------------------------
    // Only relevant while on foot, and only worth drawing once the camera has
    // detached from it (free flight); otherwise it is the player's own
    // first-person view and there is nothing to see. `character.state` lives
    // in the reference body's FIXED (rotating) frame — unlike the ship, which
    // already tracks its own position in inertial axes — so this always goes
    // through one conversion, not just when viewing a different body.
    this.characterModel.root.visible = this.onFoot && this.freeFlight.active;
    if (this.characterModel.root.visible) {
      const characterInertial = this.system.frames.transformPosition(
        this.character.state.position,
        this.ship.referenceBody.fixedFrameId,
        this.ship.referenceBody.inertialFrameId,
        t,
      );
      const characterInViewFrame =
        this.ship.referenceBody.id === body.id
          ? characterInertial
          : this.system.frames.transformPosition(
              characterInertial,
              this.ship.referenceBody.inertialFrameId,
              body.inertialFrameId,
              t,
            );
      const characterOffset = Vec3.subVectors(characterInViewFrame, cameraPosition);
      this.characterModel.root.position.set(characterOffset.x, characterOffset.y, characterOffset.z);

      // Stance orientation: heading only, no look pitch — a standing figure
      // doesn't tip forward or back just because the player looked up.
      const standBasis = tangentBasis(this.character.state.position);
      const cosH = Math.cos(this.character.state.heading);
      const sinH = Math.sin(this.character.state.heading);
      const standForward = standBasis.north.clone().mulScalar(cosH).addScaled(standBasis.east, sinH).normalize();
      const standFixed = Quat.lookRotation(standForward, standBasis.up);
      const standInertial = this.system.frames.transformOrientation(
        standFixed,
        this.ship.referenceBody.fixedFrameId,
        body.inertialFrameId,
        t,
      );
      this.characterModel.root.quaternion.set(standInertial.x, standInertial.y, standInertial.z, standInertial.w);
    }

    const fieldOutput = this.ship.fieldDrive
      ? this.ship.lastFieldDriveAccel / Math.max(0.01, this.ship.fieldDrive.def.maxAcceleration)
      : 0;
    this.shipModel.update(this.ship.engines, fieldOutput, density);

    // --- Navigation overlays --------------------------------------------------
    this.updateTrajectoryOverlay(body, cameraPosition);
    this.updateRouteOverlay(body, cameraPosition);
    this.updateTargetMarker(body, cameraPosition);

    // --- Background ---------------------------------------------------------
    const rootPosition = this.system.frames.transformPosition(
      cameraPosition,
      body.inertialFrameId,
      this.system.root,
      t,
    );
    // Bodies sharing the view body's local group (see `terrainGroupOf`) that
    // are not themselves promoted — e.g. a moon just outside its planet's
    // promotion range while the planet already has real terrain on screen.
    // Rendered at true scale in the world scene instead of compressed in the
    // astronomical one, so ordinary depth testing decides which is in front
    // rather than pass order always favouring the promoted body — see
    // `AstronomicalLayer.update`.
    const nearGroupIds = new Set<string>();
    for (const sibling of this.terrainGroupOf(body)) {
      if (sibling.id !== body.id && !this.activeBodyIds.has(sibling.id)) {
        nearGroupIds.add(sibling.id);
      }
    }

    // Same narrow band for distant bodies, for the same reason.
    this.astronomical.update(
      rootPosition,
      t,
      (d) => clamp(4.2e26 / (4 * Math.PI * d * d) / 1361, 0.25, 2.5) * 3.2,
      nearGroupIds,
      this.renderer.getPartition('worldFar').far,
    );
    this.astronomical.setStarColor(this.starColor);
    this.sunGlare.setColor(this.starColor);
    // The star's TRUE angular radius, which the render position cannot give:
    // the astronomical layer compresses distances to fit the depth partitions,
    // so its position is not to scale. Taken from the real separation instead,
    // and it is what ties the glare's geometry to reality.
    const starRoot = this.system.frames.stateInRoot(this.system.star.inertialFrameId, t).position;
    const starDistance = Math.max(this.system.star.radius, rootPosition.distanceTo(starRoot));
    const starAngularRadius = this.system.star.radius / starDistance;

    // Analytic occlusion by the reference body, replacing the depth test the
    // glare quad used to rely on.
    //
    // The ray from the camera toward the star misses the body when its closest
    // approach to the centre clears the surface. `along` < 0 means the star is
    // on the far side of the plane through the centre, which is the only case
    // where the body can occult at all. The soft band is scaled to the body's
    // own radius, which puts the fade across a few degrees of elevation near the
    // horizon regardless of how large the world is — so the glare washes out as
    // the star sets instead of being sliced by a ridge.
    //
    // None of that applies once the reference body IS the star — deep space,
    // once the vessel has left every planet's sphere of influence and
    // `checkReferenceBody` falls back to the star itself (see its `?? this.
    // system.star`). There the camera is always "behind itself" by this
    // formula (`along` collapses to exactly -camRadius, `perpendicular` to 0),
    // which used to clamp visibility to 0 — the glare vanishing in outer space
    // was this term treating the star as its own occluder.
    const camRadius = cameraPosition.length();
    const along = cameraPosition.dot(sunDirection);
    const perpendicular = Math.sqrt(Math.max(0, camRadius * camRadius - along * along));
    let glareVisibility =
      body.id === this.system.star.id || along >= 0
        ? 1
        : clamp01((perpendicular - body.radius) / (body.radius * 0.004) + 0.5);

    // The sphere test above only knows the world's mean radius. Mountains can
    // hide a low sun the same way the horizon does, and any distant body can
    // transit the star's disc from where the glare is drawn. Both reduce only
    // the glare's INTENSITY, not the quad: glare is a camera-side artefact of
    // the starlight, so occluding the source of that light is what fades it.
    glareVisibility *= this.terrainGlareOcclusion(body, cameraPosition, sunDirection, t, starAngularRadius);
    glareVisibility *= this.distantBodyGlareOcclusion(
      body,
      cameraPosition,
      sunDirection,
      t,
      starAngularRadius,
    );

    this.sunGlare.update(
      this.astronomical.starRenderPosition(rootPosition, t),
      irradiance,
      this.renderer.astronomicalCamera.quaternion,
      starAngularRadius,
      density,
      glareVisibility,
      this.renderer.fov,
      this.renderer.astronomicalCamera.aspect,
      this.renderer.renderer.toneMappingExposure,
    );

    const sunElevation = Math.asin(clamp(sunDirection.dot(cameraPosition.normalized()), -1, 1));
    const starWashout = this.starfield.setAtmosphericWashout(density, sunElevation);
    this.renderer.setBackgroundIntensity(starWashout);
    this.starfield.setPixelRatio(this.renderer.renderer.getPixelRatio());
    // Distant planets are point sources exactly as the stars are, so the same
    // sky brightness has to swallow them — otherwise every world in the system
    // hangs visibly in a noon sky. See `DistantBodyPoints`.
    this.astronomical.setAtmosphericWashout(starWashout);
    this.astronomical.setPixelRatio(this.renderer.renderer.getPixelRatio());

    // Exposure adapts to the actual scene brightness, so moving from a dark
    // cockpit to a lit surface, or from Halcyon to Kolyma, stays readable.
    //
    // Both the surface and atmosphere shaders output radiance — the surface
    // divides by pi like any correct Lambertian term — so a sunlit 0.3-albedo
    // surface lands near 0.26, and the numerator is chosen to put that around
    // 0.45 on screen, which is where a mid-grey subject in daylight belongs.
    //
    // Deriving that numerator requires one non-obvious step, and getting it
    // wrong is what made daylight burn out: Three's ACES fitting does NOT take
    // `toneMappingExposure` as its scale factor. It applies
    // `toneMappingExposure / 0.6`, so the shader sees 1.67x whatever is set
    // here. Working the full chain backwards — 0.45 sRGB is 0.17 linear, which
    // the RRT/ODT fit reaches at an input of 0.26 — the scale the fit needs is
    // ~0.85, so the exposure is ~0.51 and the numerator ~1.3. A numerator
    // tuned as though the 0.6 were absent overshoots by that same 1.67x, which
    // pushes a 0.3-albedo surface to 0.79 and snow past 0.9: every drift and
    // slope flattens into one white sheet.
    //
    // The exponent is what matters. Irradiance across this system spans a
    // factor of about four hundred, from Vesper's furnace to Kolyma's twilight.
    // Compensating by a square root leaves twenty stops of that on screen,
    // which blows the inner worlds to white and crushes the outer ones. An
    // exponent of 0.8 leaves a factor of about three: enough that Vesper still
    // reads as blinding and Kolyma as dim, without either leaving the range the
    // display can show.
    // What the exposure adapts to is the light actually reaching *here*, which
    // is not the same as the body's distance from the star.
    //
    // `irradiance` is computed from that distance alone, so it does not change
    // between noon and midnight, and the exposure did not either: the night
    // side was being exposed for full daylight. That is why the night terrain
    // measured 100% black, and why planetshine could not be seen no matter how
    // correct the term was. Physically Aegis delivers Ferrum about 1.5% of its
    // daylight — real, and invisible at an exposure set for the day side.
    //
    // Eyes adapt; this is that. The sun's contribution is gated on its
    // elevation, so it falls away through dusk and leaves the night terms
    // holding the value. Planetshine is scaled up by the same compression the
    // sunlight already got — `sunIntensity` is clamped to a tenth of the true
    // 400:1 range, and planetshine is not, so without matching them the
    // clamping alone would bury it — and airglow is a small floor standing in
    // for what a real sky never quite loses.
    //
    // The lower bound caps how far night adaptation may run, and it is the one
    // number holding the night sky together. Unbounded it goes to the clamp and
    // amplifies a black frame into visible quantisation noise; too high and a
    // dark-adapted eye is being simulated by a camera that never opens up, so
    // only the brightest stars survive. At 0.30 a moonless night sits about
    // three stops above the day exposure, which is where the faint stars and the
    // dust lanes in the galaxy band become readable before the terrain grains.
    const airglow = 0.02 + skyStrength * 0.05;
    const localIllumination = Math.max(
      0.3,
      sunIntensity * daylight + planetshineForDisplay * 0.25 + airglow,
    );

    // Camera metering: a real camera (and a real eye) stops down when a
    // bright source sits in frame, which is why looking toward the sun dims
    // the REST of the scene instead of blowing the sun out even further.
    // `localIllumination` above answers "how lit is the world"; this answers
    // the different question "is the lens currently pointed at the brightest
    // thing in it" — real auto-exposure responds to both, and until now this
    // system only had the first, so the sun sat in frame at the same exposure
    // as looking away from it.
    //
    // `forward` is the camera's actual view axis, in the same inertial axes
    // as `sunDirection`: `orientation` is the literal quaternion handed to
    // every Three.js camera this frame (see `setViewOrientation`), and Three
    // cameras look down local -Z.
    const forward = orientation.rotate(this.scratchC.set(0, 0, -1));
    const viewCos = clamp(forward.dot(sunDirection), -1, 1);
    const halfFovRad = (this.renderer.fov * Math.PI) / 360;
    // Ramped over roughly the frame's half-diagonal rather than switching the
    // instant the disc crosses the frustum edge. Reuses `glareVisibility`'s
    // analytic body-occlusion test: a sun hidden behind a ridge should not
    // dim the sky the way one sitting in the open does.
    const sunOffAxis = Math.acos(viewCos) / (halfFovRad * 1.4);
    const sunInFrame = clamp01(1 - sunOffAxis) * glareVisibility;
    // Scales with the same fierceness the glare itself uses, so a modest
    // star barely affects metering while a furnace like Vesper's stops the
    // exposure down hard the moment it enters frame.
    const meteringStop = sunInFrame * (0.22 + sunBloomFactor(irradiance) * 0.45);

    const targetExposure = clamp(
      (1.3 / Math.pow(localIllumination, 0.8)) * (1 - meteringStop),
      0.05,
      12,
    );
    this.applyDebugLayers();

    const current = this.renderer.renderer.toneMappingExposure;
    this.renderer.setExposure(current + (targetExposure - current) * 0.04);

    this.renderer.render();
  }

  /**
   * Feeds the synthesiser from the same state the HUD reads.
   *
   * Nothing is scheduled or triggered here; every voice is a continuous
   * parameter ride. That is what lets an entry sound like one event rather than
   * a sequence of cues firing.
   */
  private updateAudio(dt: number): void {
    // Free flight detaches the camera from the vessel completely, so the
    // vessel's wind, engines and cabin tone describe somewhere the player is
    // no longer standing. Ramped to silence through the mixer rather than cut,
    // and kept separate from the player's own mute so returning to the vessel
    // cannot unmute audio they muted by hand.
    this.audio.setSuspended(this.freeFlight.active);

    const env = this.lastSnapshot?.environment;
    const vessel = this.lastSnapshot?.vessel;
    if (!env || !vessel) return;

    this.audio.update(
      {
        density: env.density,
        dynamicPressure: env.dynamicPressure,
        airspeed: this.lastSnapshot?.navigation.surfaceSpeed ?? 0,
        mach: env.mach,
        throttle: vessel.throttle,
        thrust: vessel.thrust,
        rcs: Math.min(1, this.input.axes.translation.length()),
        fieldDrive: vessel.fieldDrive.acceleration,
        power: vessel.powerDemand,
        onFoot: this.onFoot,
        // In vacuum the cabin is the only thing making any noise, so whether
        // the player is inside it is the difference between sound and silence.
        inCabin: !this.onFoot,
        contacts: vessel.contacts,
        windSpeed: env.windSpeed,
      },
      dt,
    );
  }

  private resizeIfNeeded(): void {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.renderer.resize(w, h);
    }
  }

  // =========================================================================
  // Actions
  // =========================================================================

  private handleActions(): void {
    const input = this.input;

    if (input.wasPressed('pause')) this.paused = !this.paused;
    if (input.wasPressed('toggleRotationDamping')) {
      this.computer.holds.rotationDamping = !this.computer.holds.rotationDamping;
    }
    if (input.wasPressed('toggleMainEngine')) {
      this.ship.controls.mainEngineArmed = !this.ship.controls.mainEngineArmed;
    }
    if (input.wasPressed('toggleAtmosphericEngines')) {
      this.ship.controls.atmosphericEnginesArmed = !this.ship.controls.atmosphericEnginesArmed;
    }
    if (input.wasPressed('toggleLandingThrusters')) {
      this.ship.controls.landingThrustersArmed = !this.ship.controls.landingThrustersArmed;
    }
    if (input.wasPressed('toggleFieldDrive')) {
      const mode = this.ship.controls.fieldDriveMode;
      this.ship.controls.fieldDriveMode = mode === 'off' ? 'gravityCompensation' : 'off';
      this.notify('info', 'Field drive', `Mode set to ${this.ship.controls.fieldDriveMode}.`);
    }

    if (input.wasPressed('alignPrograde')) this.computer.setAlignment('prograde');
    if (input.wasPressed('alignRetrograde')) this.computer.setAlignment('retrograde');
    if (input.wasPressed('alignRadialOut')) this.computer.setAlignment('radialOut');
    if (input.wasPressed('alignRadialIn')) this.computer.setAlignment('radialIn');
    if (input.wasPressed('alignNormal')) this.computer.setAlignment('normal');
    if (input.wasPressed('alignTarget')) this.computer.setAlignment('target');
    if (input.wasPressed('alignSurfaceUp')) this.computer.setAlignment('surfaceUp');

    if (input.wasPressed('hover')) {
      this.computer.holds.hoverAssist = !this.computer.holds.hoverAssist;
      this.ship.controls.landingThrustersArmed = this.computer.holds.hoverAssist;
    }
    if (input.wasPressed('holdAltitude')) {
      this.computer.holds.altitudeHold = !this.computer.holds.altitudeHold;
      if (this.computer.holds.altitudeHold) {
        this.computer.holds.altitudeTarget = this.lastSnapshot?.navigation.altitude ?? 100;
      }
    }

    const warpContext = this.buildWarpContext();
    if (input.wasPressed('warpUp')) {
      const before = this.warp.rate;
      const after = this.warp.increase(warpContext);
      if (after === before && this.warp.lastAutoStopReason) {
        this.notify('warning', 'Time warp limited', this.warp.lastAutoStopReason);
      }
    }
    if (input.wasPressed('warpDown')) this.warp.decrease();
    if (input.wasPressed('warpReset')) this.warp.reset();

    if (input.wasPressed('macroLaunch')) this.runMacro('launchToOrbit');
    if (input.wasPressed('macroLand')) this.runMacro('land');
    if (input.wasPressed('macroCircularise')) this.runMacro('circularise');
    if (input.wasPressed('macroDock')) this.runMacro('dock');
    if (input.wasPressed('macroTransfer')) this.runMacro('transferToTarget');
    if (input.wasPressed('macroAbort')) {
      const env = this.buildEnvironment();
      this.macros.cancel(this.macroContext(env, PHYSICS_STEP));
      this.planner.cancelExecution();
      this.notify('warning', 'Macro aborted', 'Manual control restored.');
    }

    if (input.wasPressed('toggleView')) {
      this.cameraMode = this.cameraMode === 'cockpit' ? 'chase' : 'cockpit';
    }
    if (input.wasPressed('boardShip')) this.toggleOnFoot();

    if (input.wasPressed('toggleAudio')) {
      const muted = this.audio.toggleMuted();
      this.notify('info', 'Audio', muted ? 'Muted.' : 'Unmuted.');
    }
    if (input.wasPressed('toggleFreeFlight')) this.toggleFreeFlight();
    if (this.freeFlight.active) {
      if (input.wasPressed('freeFlightFaster')) {
        this.freeFlight.faster();
        this.notify('info', 'Free flight', `${formatSpeed(this.freeFlight.speed)}`);
      }
      if (input.wasPressed('freeFlightSlower')) {
        this.freeFlight.slower();
        this.notify('info', 'Free flight', `${formatSpeed(this.freeFlight.speed)}`);
      }
    }
    // Fire-and-forget: a save must never stall the frame, and a failed one
    // reports itself through the notification channel like anything else.
    if (input.wasPressed('quickSave')) void this.saveGame();
  }

  private buildWarpContext(): WarpContext {
    const body = this.ship.referenceBody;
    const surface = this.surfaces.get(body.id);
    const positionFixed = this.system.frames.transformPosition(
      this.ship.body.position,
      body.inertialFrameId,
      body.fixedFrameId,
      this.time,
    );
    const radius = this.ship.body.position.length();
    const surfaceRadius = surface ? surface.heightField.walkableRadiusAt(positionFixed) : body.radius;
    const altitudeAboveDatum = radius - body.radius;
    const atmosphere = this.atmospheres.get(body.id);

    const nextNode = this.planner.next;
    const thrusting = this.ship.engines.some((e) => e.throttle > 0.01);

    return {
      ship: this.ship,
      referenceBody: body,
      altitude: radius - surfaceRadius,
      altitudeAboveDatum,
      density: atmosphere ? atmosphere.densityAt(altitudeAboveDatum) : 0,
      timeToManeuver: nextNode ? nextNode.time - this.time : Infinity,
      timeToSoiChange: Infinity,
      thrusting,
      landed: this.ship.contact.landed || this.onFoot,
    };
  }

  runMacro(id: MacroId, options: { targetAltitude?: number } = {}): void {
    const env = this.buildEnvironment();
    const ctx = this.macroContext(env, PHYSICS_STEP);
    const estimate = this.macros.preview(id, ctx, options);
    if (estimate.blocked) {
      this.notify('warning', 'Macro unavailable', estimate.blockedReason ?? 'Preconditions not met');
      return;
    }
    if (this.macros.startById(id, ctx, options)) {
      this.notify(
        'info',
        'Macro engaged',
        `Estimated ${Math.round(estimate.duration)} s, ${Math.round(estimate.deltaV)} m/s delta-v, ` +
          `${Math.round(estimate.propellant)} kg propellant.`,
      );
    } else if (this.macros.lastMessage) {
      this.notify('warning', 'Macro failed to start', this.macros.lastMessage);
    }
  }

  cancelMacro(): void {
    const env = this.buildEnvironment();
    this.macros.cancel(this.macroContext(env, PHYSICS_STEP));
  }

  /** Estimate for the UI's confirmation panel. */
  previewMacro(id: MacroId, options: { targetAltitude?: number } = {}) {
    const env = this.buildEnvironment();
    return this.macros.preview(id, this.macroContext(env, PHYSICS_STEP), options);
  }

  toggleOnFoot(): void {
    if (!this.activeSurface) {
      this.notify('warning', 'Cannot disembark', 'No landable surface here.');
      return;
    }
    if (!this.onFoot) {
      if (!this.ship.contact.landed) {
        this.notify('warning', 'Cannot disembark', 'The vessel must be landed and stationary.');
        return;
      }
      const positionFixed = this.system.frames.transformPosition(
        this.ship.body.position,
        this.ship.referenceBody.inertialFrameId,
        this.ship.referenceBody.fixedFrameId,
        this.time,
      );
      // Step out a few metres to the side of the hull.
      const basis = tangentBasis(positionFixed);
      const exit = positionFixed.clone().addScaled(basis.east, this.ship.def.size[0] * 0.9);
      this.character.spawnAt(exit.normalize());
      this.onFoot = true;
      this.cameraMode = 'onFoot';
      this.notify('info', 'Disembarked', 'Suit systems nominal.');
    } else {
      const shipFixed = this.system.frames.transformPosition(
        this.ship.body.position,
        this.ship.referenceBody.inertialFrameId,
        this.ship.referenceBody.fixedFrameId,
        this.time,
      );
      if (this.character.state.position.distanceTo(shipFixed) > 25) {
        this.notify('warning', 'Too far from the vessel', 'Move closer to board.');
        return;
      }
      this.onFoot = false;
      this.cameraMode = 'cockpit';
      this.notify('info', 'Aboard', 'Hatch sealed.');
    }
  }

  setQuality(level: Exclude<QualityLevel, 'custom'>): void {
    this.quality = { ...QUALITY_PROFILES[level] };
    this.renderer.setQuality(this.quality, detectCapabilities());
    const vegetationQuality = this.vegetationQuality();
    for (const surface of this.surfaces.values()) {
      surface.quality = { ...surface.quality, ...this.quality.terrain };
      surface.vegetationQuality = vegetationQuality;
    }
    for (const [id, layer] of this.vegetation) {
      layer.setScatterDistance(
        vegetationScatterDistanceFor(this.quality.scatterDistance, this.surfaces.get(id)?.vegetationRange ?? 0),
      );
    }
    // SSR may have been created or torn down by the quality change; re-push the
    // (possibly new) capture to every ocean so a mid-session upgrade picks it up.
    const ssr = this.renderer.screenSpaceReflection;
    for (const ocean of this.oceans.values()) ocean.setSsrReflection(ssr);
    this.notify('info', 'Quality changed', `Rendering profile set to ${level}.`);
    this.persistGraphicsSettings();
  }

  /**
   * Derives `PlanetSurface`'s vegetation settings from the quality tier.
   *
   * Density is the tier's `vegetationCellMetres`, passed straight through:
   * the scatter grid is a property of the planet, so this one number sets the
   * plant count at every LOD level at once and no per-chunk apportioning is
   * needed (or wanted — dividing a global budget between chunks is what used
   * to make the same ground carry a different forest at each level). The
   * per-chunk instance count remains bounded because the grid itself bounds
   * it: at most one plant per population per cell.
   *
   * `maxInstancesPerChunk` is only the allocation ceiling behind that, sized
   * from the global budget so a pathologically dense body still cannot make a
   * single chunk unbounded.
   */
  private vegetationQuality(): VegetationQualitySettings {
    return {
      enabled: this.vegetationEnabled && this.quality.scatterBudget > 0,
      cellMetres: this.quality.vegetationCellMetres,
      densityScale: this.quality.vegetationDensityScale,
      maxInstancesPerChunk: Math.max(0, Math.round(this.quality.scatterBudget / 8)),
    };
  }

  /** Resolution scale, independent of the quality preset. 0.5-2.0. */
  setRenderScale(scale: number): void {
    this.renderScale = clamp(scale, 0.5, 2);
    this.renderer.setRenderScale(this.renderScale);
    this.persistGraphicsSettings();
  }

  /**
   * Mountains casting shadows on nearby terrain. Off skips the cascade
   * render passes entirely, not just their effect on lighting, so this
   * actually recovers the frame cost.
   */
  setTerrainSelfShadow(enabled: boolean): void {
    this.terrainSelfShadowEnabled = enabled;
    this.renderer.setTerrainShadowEnabled(enabled);
    this.persistGraphicsSettings();
  }

  /**
   * A moon or planet blocking the sun for a nearby body. Off passes a radius
   * of 0 every frame instead of computing `eclipseOccluderAt`, which is
   * already cheap, but the per-fragment shader test in `TerrainMaterial` is
   * not free, so honouring the toggle still matters.
   */
  setEclipseShadows(enabled: boolean): void {
    this.eclipseShadowsEnabled = enabled;
    this.persistGraphicsSettings();
  }

  /**
   * Player-facing vegetation switch. Toggles visibility immediately (cheap:
   * a group's `.visible`, same as every other `debugLayers` toggle) and also
   * updates `PlanetSurface.vegetationQuality`, so a newly streamed chunk
   * stops paying the worker-side placement cost too, not just the render
   * cost — turning it off actually saves the resources, it does not just
   * hide them.
   */
  setVegetationEnabled(enabled: boolean): void {
    this.vegetationEnabled = enabled;
    const vegetationQuality = this.vegetationQuality();
    for (const surface of this.surfaces.values()) surface.vegetationQuality = vegetationQuality;
    if (this.activeVegetation) this.activeVegetation.root.visible = enabled && this.debugLayers.vegetation;
    this.persistGraphicsSettings();
  }

  private persistGraphicsSettings(): void {
    const settings: GraphicsSettings = {
      qualityLevel: this.quality.level === 'custom' ? null : this.quality.level,
      renderScale: this.renderScale,
      terrainSelfShadow: this.terrainSelfShadowEnabled,
      eclipseShadows: this.eclipseShadowsEnabled,
      vegetationEnabled: this.vegetationEnabled,
    };
    saveGraphicsSettings(settings);
  }

  notify(severity: NotificationSnapshot['severity'], title: string, body: string): void {
    const note: NotificationSnapshot = {
      id: this.notificationId++,
      severity,
      title,
      body,
      time: this.time,
    };
    this.notifications.push(note);
    if (this.notifications.length > 40) this.notifications.shift();
    this.events.emit('notification', note);
  }

  // =========================================================================
  // Snapshot
  // =========================================================================

  private publishSnapshot(): void {
    const snapshot: GameSnapshot = {
      frame: this.frame,
      simulationTime: this.time,
      paused: this.paused,
      vessel: this.vesselSnapshot(),
      navigation: this.navigationSnapshot(),
      environment: this.environmentSnapshot(),
      warp: this.warpSnapshot(),
      player: this.playerSnapshot(),
      performance: this.performanceSnapshot(),
      diagnostics: this.diagnosticsSnapshot(),
      notifications: this.notifications.slice(-8),
      macro: this.macros.progress(this.macroContext(this.buildEnvironment(), PHYSICS_STEP)),
      tour: this.tour.status(),
    };
    this.lastSnapshot = snapshot;
    this.events.emit('snapshot', snapshot);
  }

  private vesselSnapshot(): VesselSnapshot {
    const tel = this.ship.telemetry();
    const fd = this.ship.fieldDrive;
    return {
      name: this.ship.def.name,
      className: this.ship.def.className,
      alignment: this.computer.holds.alignment,
      mass: tel.mass.total,
      dryMass: tel.mass.dry,
      fuelMass: tel.mass.fuel,
      cargoMass: tel.mass.cargo,
      deltaV: this.ship.totalDeltaV(),
      throttle: this.ship.controls.throttle,
      thrust: tel.thrustTotal,
      thrustToWeight: tel.thrustToWeight,
      gForce: tel.gForce,
      hullTemperature: tel.hullTemperature,
      hullTemperatureLimit: this.ship.thermal.temperatureLimit,
      hullDamage: this.ship.damage.hull,
      gearDamage: this.ship.damage.landingGear,
      wingDamage: this.ship.damage.wings,
      batteryFraction: this.ship.power.chargeFraction,
      powerGeneration: tel.power?.generation ?? 0,
      powerDemand: tel.power?.demand ?? 0,
      brownout: tel.power?.brownout ?? false,
      propellantFraction: tel.propellantFraction,
      monopropellant: this.ship.fuelOfType('monopropellant'),
      landed: this.ship.contact.landed,
      contacts: this.ship.contact.contacts,
      fieldDrive: {
        present: fd !== null,
        mode: fd?.mode ?? 'off',
        acceleration: tel.fieldDriveAcceleration,
        efficiency: tel.fieldDriveEfficiency,
        temperature: tel.fieldDriveTemperature,
        thermalLimit: fd?.def.thermalLimit ?? 0,
        power: tel.fieldDrivePower,
        limitReason: tel.fieldDriveLimit,
      },
      engineGroups: {
        main: this.ship.controls.mainEngineArmed,
        atmospheric: this.ship.controls.atmosphericEnginesArmed,
        landing: this.ship.controls.landingThrustersArmed,
      },
      assistFailures: [...this.computer.failures],
      holds: {
        rotationDamping: this.computer.holds.rotationDamping,
        attitudeHold: this.computer.holds.attitudeHold,
        altitudeHold: this.computer.holds.altitudeHold,
        verticalSpeedHold: this.computer.holds.verticalSpeedHold,
        hoverAssist: this.computer.holds.hoverAssist,
        matchTargetVelocity: this.computer.holds.matchTargetVelocity,
      },
    };
  }

  private navigationSnapshot(): NavigationSnapshot {
    const body = this.ship.referenceBody;
    const position = this.ship.body.position;
    const velocity = this.ship.body.velocity;
    const radius = position.length();
    const up = radius > 0 ? position.clone().mulScalar(1 / radius) : new Vec3(0, 1, 0);

    const surfaceVelocity = Vec3.subVectors(velocity, body.surfaceVelocityAt(position));
    const verticalSpeed = surfaceVelocity.dot(up);
    const horizontalSpeed = surfaceVelocity.rejectFromUnit(up).length();

    const elements = elementsFromState(position, velocity, body.mu, this.time);
    const surface = this.surfaces.get(body.id);
    const positionFixed = this.system.frames.transformPosition(
      position,
      body.inertialFrameId,
      body.fixedFrameId,
      this.time,
    );
    const surfaceRadius = surface ? surface.heightField.walkableRadiusAt(positionFixed) : body.radius;
    const latLon = latLonFromPosition(positionFixed);

    // The same basis the alignment holds steer by, rotated into the vessel's
    // own frame so the navball can draw where each direction actually is.
    const basis = this.computer.navigationBasis(this.ship);
    const toBodyFrame = (direction: Vec3): BodyFrameDirection => {
      const local = this.ship.body.orientation.rotateInverse(direction);
      return [local.x, local.y, local.z];
    };
    // Local geographic north at the vessel's position, for the navball's
    // heading readout. tangentBasis wants a fixed-frame direction (it derives
    // north from the body's own rotation axis), so this goes through
    // positionFixed rather than the inertial-frame position used above, then
    // back to inertial before toBodyFrame like every other basis vector here.
    const northFixed = tangentBasis(positionFixed).north;
    const northInertial = this.system.frames.transformDirection(
      northFixed,
      body.fixedFrameId,
      body.inertialFrameId,
      this.time,
    );

    const prediction = this.predictor.predict(body, position, velocity, this.time, {
      samples: 96,
      terrainCeiling: surface?.heightField.ceiling ?? 0,
    });

    let target: NavigationSnapshot['target'] = null;
    if (this.targetBodyId) {
      const targetBody = this.system.find(this.targetBodyId);
      if (targetBody) {
        const state = this.system.frames.transformState(
          new Vec3(),
          new Vec3(),
          targetBody.inertialFrameId,
          body.inertialFrameId,
          this.time,
        );
        target = {
          id: targetBody.id,
          name: targetBody.name,
          distance: state.position.distanceTo(position),
          relativeSpeed: Vec3.subVectors(state.velocity, velocity).length(),
        };
      }
    }

    return {
      referenceBodyId: body.id,
      referenceBodyName: body.name,
      dominantBodyId: this.gravity.sample(
        this.system.frames.transformPosition(position, body.inertialFrameId, this.system.root, this.time),
        this.time,
      ).dominant.id,
      altitude: radius - surfaceRadius,
      altitudeAboveDatum: radius - body.radius,
      orbitalSpeed: velocity.length(),
      surfaceSpeed: surfaceVelocity.length(),
      verticalSpeed,
      horizontalSpeed,
      apoapsis: apoapsisRadius(elements) - body.radius,
      periapsis: periapsisRadius(elements) - body.radius,
      orbitalPeriod: orbitalPeriod(elements),
      eccentricity: elements.eccentricity,
      inclination: elements.inclination,
      timeToApoapsis: timeToApoapsis(elements, this.time),
      timeToPeriapsis: timeToPeriapsis(elements, this.time),
      escapeVelocity: escapeVelocity(body.mu, radius),
      circularVelocity: circularVelocity(body.mu, radius),
      latitude: latLon.latitude,
      longitude: latLon.longitude,
      bodyFrame: {
        prograde: toBodyFrame(basis.prograde),
        normal: toBodyFrame(basis.normal),
        radialOut: toBodyFrame(basis.radialOut),
        north: toBodyFrame(northInertial),
      },
      events: prediction.events.slice(0, 6).map((e) => ({
        kind: e.kind,
        label: e.label,
        timeUntil: e.time - this.time,
        distance: e.distance ?? 0,
      })),
      target,
      maneuverNodes: this.planner.nodes.map((n) => {
        const estimate = this.planner.estimate(n, this.ship);
        return {
          id: n.id,
          label: n.label,
          deltaV: estimate.deltaV,
          timeUntil: n.time - this.time,
          burnDuration: estimate.duration,
        };
      }),
    };
  }

  private environmentSnapshot(): EnvironmentSnapshot {
    const body = this.ship.referenceBody;
    const env = this.buildEnvironment();
    const surface = this.surfaces.get(body.id);

    let biome = 'unknown';
    if (surface) {
      const elevation = surface.heightField.elevationAtPosition(env.positionFixed);
      const dir = env.positionFixed.normalized();
      const { temperature } = climateAt(dir.y, elevation, surface.params);
      const humidity = humidityAt(dir.x, dir.y, dir.z, elevation, surface.params);
      const slope = surface.heightField.slopeAt(env.positionFixed);
      const underwater = surface.hasOcean && elevation < surface.params.seaLevel;
      biome = classifyBiome(surface.params, this.biomeBands, elevation, temperature, humidity, slope, underwater);
    }

    const sunDirection = this.sunDirectionAt(body, this.ship.body.position, this.time);
    const sunElevation = Math.asin(clamp(sunDirection.dot(env.up), -1, 1));
    const spin = wrapTau((2 * Math.PI * this.time) / Math.max(1, body.dayLength));

    return {
      bodyName: body.name,
      biome,
      temperature: env.temperature,
      pressure: env.pressure,
      density: env.density,
      windSpeed: body.atmosphere.windSpeed,
      gravity: env.gravity.length(),
      dynamicPressure: this.ship.lastAero?.dynamicPressure ?? 0,
      mach: this.ship.lastAero?.mach ?? 0,
      irradiance: env.irradiance,
      radiation: body.def.hazards.radiation,
      breathable: body.atmosphere.breathable && env.altitudeAboveDatum < 5000,
      sunElevation,
      dayFraction: spin / (2 * Math.PI),
    };
  }

  private warpSnapshot(): WarpSnapshot {
    const restriction = this.warp.restriction(this.buildWarpContext());
    return {
      rate: this.warp.rate,
      mode: this.warp.mode,
      maxRate: restriction.maxRate,
      restriction: restriction.reason,
      autoStopReason: this.warp.lastAutoStopReason,
    };
  }

  private playerSnapshot(): PlayerSnapshot {
    return {
      onFoot: this.onFoot,
      visitedBodies: Array.from(this.visitedBodies),
      freeFlight: this.freeFlightSnapshot(),
    };
  }

  /**
   * Where the free-flight camera is, for the HUD to show in place of the
   * vessel's navigation panel — see FreeFlightSnapshot's doc comment.
   */
  private freeFlightSnapshot(): FreeFlightSnapshot | null {
    if (!this.freeFlight.active || !this.freeFlightBody) return null;
    const body = this.freeFlightBody;
    const positionFixed =
      this.freeFlightFrame === 'fixed'
        ? this.freeFlight.position
        : this.system.frames.transformPosition(
            this.freeFlight.position,
            body.inertialFrameId,
            body.fixedFrameId,
            this.time,
          );
    const radius = positionFixed.length();
    const surface = this.surfaces.get(body.id);
    const surfaceRadius = surface ? surface.heightField.walkableRadiusAt(positionFixed) : body.radius;
    // Sea level, not the nominal datum radius those aren't the same thing:
    // PlanetSurface.seaLevelRadius is body.radius offset by the body's own
    // authored sea level, which can sit above or below the datum. Falls back
    // to the datum itself on a dry world, where "sea level" has no referent.
    const seaLevelRadius = surface?.hasOcean ? surface.seaLevelRadius : body.radius;
    const latLon = latLonFromPosition(positionFixed);

    // Where the camera is LOOKING, in local terms. Position alone does not
    // describe a view, which makes a reported sighting impossible to reproduce
    // from a screenshot — heading and pitch close that gap.
    const basis = tangentBasis(positionFixed);
    const orientationFixed =
      this.freeFlightFrame === 'fixed'
        ? this.freeFlight.orientation
        : this.system.frames.transformOrientation(
            this.freeFlight.orientation,
            body.inertialFrameId,
            body.fixedFrameId,
            this.time,
          );
    // The camera's own forward axis, matching the convention every pose in this
    // project uses (+Z nose); the render camera's -Z flip is applied later.
    const forward = orientationFixed.rotate(new Vec3(0, 0, 1));
    const headingRad = Math.atan2(forward.dot(basis.east), forward.dot(basis.north));
    const pitchRad = Math.asin(clamp(forward.dot(basis.up), -1, 1));

    return {
      bodyName: body.name,
      heading: (headingRad * 180) / Math.PI,
      pitch: (pitchRad * 180) / Math.PI,
      altitude: radius - surfaceRadius,
      altitudeAboveSeaLevel: radius - seaLevelRadius,
      latitude: latLon.latitude,
      longitude: latLon.longitude,
      speed: this.freeFlight.effectiveSpeed,
      speedLimited: this.freeFlight.speedLimited,
      speedOverridden: this.freeFlight.speedOverridden,
    };
  }

  private performanceSnapshot(): PerformanceSnapshot {
    const stats = this.activeSurface?.stats();
    const poolStats = this.pool.stats();
    return {
      fps: this.fps,
      frameMs: 1000 / Math.max(1, this.fps),
      physicsMs: this.physicsMs,
      renderMs: this.renderer.stats.frameMs,
      drawCalls: this.renderer.stats.drawCalls,
      triangles: this.renderer.stats.triangles,
      chunksResident: stats?.resident ?? 0,
      chunksVisible: stats?.visible ?? 0,
      chunksPending: stats?.pending ?? 0,
      workerQueue: poolStats.queued,
      workerAverageMs: poolStats.averageMs,
      estimatedGpuMB: (this.activeTerrain?.estimateGpuBytes() ?? 0) / (1024 * 1024),
      droppedSteps: this.accumulator.droppedSteps,
    };
  }

  private diagnosticsSnapshot(): DiagnosticsSnapshot {
    const stats = this.activeSurface?.stats();
    const cameraPosition = this.cameraPositionInFrame(this.scratchB);
    const rootPosition = this.system.frames.transformPosition(
      cameraPosition,
      this.ship.referenceBody.inertialFrameId,
      this.system.root,
      this.time,
    );
    const gravitySample = this.gravity.sample(rootPosition, this.time);

    return {
      frameId: this.floatingOrigin.activeFrameId,
      originOffset: this.floatingOrigin.originOffset.length(),
      originShifts: this.floatingOrigin.shifts,
      distanceFromOrigin: Vec3.subVectors(cameraPosition, this.floatingOrigin.originOffset).length(),
      cameraNear: this.renderer.worldNearCamera.near,
      cameraFar: this.renderer.worldFarCamera.far,
      nearFarRatio: this.renderer.worldFarCamera.far / Math.max(1e-6, this.renderer.worldNearCamera.near),
      chunksByLevel: stats ? Array.from(stats.byLevel.entries()).sort((a, b) => a[0] - b[0]) : [],
      maxLodLevel: stats?.maxLevelReached ?? 0,
      gravitySources: gravitySample.contributions.slice(0, 5).map((c) => ({
        name: c.body.name,
        magnitude: c.magnitude,
        distance: c.distance,
      })),
      physicsTimestep: PHYSICS_STEP,
      heightFieldCache: this.activeSurface?.heightField.cacheSize ?? 0,
    };
  }

  get snapshot(): GameSnapshot | null {
    return this.lastSnapshot;
  }

  setTarget(bodyId: string | null): void {
    this.targetBodyId = bodyId;
    if (!bodyId) {
      this.computer.targetPosition = null;
      this.computer.targetVelocity = null;
      return;
    }
    const body = this.system.find(bodyId);
    if (!body) return;
    const state = this.system.frames.transformState(
      new Vec3(),
      new Vec3(),
      body.inertialFrameId,
      this.ship.referenceBody.inertialFrameId,
      this.time,
    );
    this.computer.targetPosition = state.position;
    this.computer.targetVelocity = state.velocity;
  }

  // =========================================================================
  // Free flight (debug)
  // =========================================================================

  /**
   * Detaches or reattaches the debug camera.
   *
   * The vessel keeps flying its own physics throughout. Free flight moves a
   * camera, not a spacecraft, which is the only reason it is allowed to assign
   * position directly.
   */
  toggleFreeFlight(): void {
    // Reattaching to the vessel while a tour is running would leave the tour
    // silently driving a camera the player just asked to give back.
    if (this.tourActive) this.stopTour();
    if (this.freeFlight.active) {
      this.freeFlight.disengage();
      this.freeFlightBody = null;
      // Reattaching returns the view to the vessel, which may be at a different
      // body than the camera wandered to.
      this.setActiveBody(this.ship.referenceBody);
      this.notify('info', 'Free flight off', 'Camera reattached to the vessel.');
      this.persistDevSessionFreeFlight();
      return;
    }
    this.freeFlight.engage(this.cameraPositionInFrame(), this.shipFacing());
    // The vessel's body, explicitly — NOT `this.viewBody`. The position just
    // handed to engage() came from the vessel's frame, and viewBody returns
    // `freeFlightBody` whenever free flight is active, which it now is. Reading
    // it here preserved whatever body a previous detach had left behind: the
    // camera would be described in one body's frame while carrying another's
    // label, so its altitude, air density and sun angle were all computed
    // against the wrong world.
    this.freeFlightBody = this.ship.referenceBody;
    this.freeFlightFrame = 'inertial';
    this.notify(
      'info',
      'Free flight on',
      `Camera detached at ${formatSpeed(this.freeFlight.speed)}. Wheel sets speed, F8 to return.`,
    );
    this.persistDevSessionFreeFlight();
  }

  /**
   * Debug jump: puts the vessel into a circular orbit of another body.
   *
   * This is a teleport and is labelled as one. What it deliberately does *not*
   * do is leave the simulation in a state it could not have reached on its own:
   * the vessel arrives with the orbital velocity its new altitude requires, in
   * the target's frame, so the moment the jump finishes everything downstream —
   * gravity, the trajectory predictor, warp interlocks — is looking at an
   * ordinary orbit. A jump that dropped the vessel at rest beside a planet would
   * instead begin a free fall into it and report a wildly wrong apoapsis.
   */
  fastTravelTo(bodyId: string, altitudeFraction = 0.35): boolean {
    const body = this.system.find(bodyId);
    if (!body) return false;

    const current = this.ship.referenceBody;
    const converted = this.system.frames.transformState(
      this.ship.body.position,
      this.ship.body.velocity,
      current.inertialFrameId,
      body.inertialFrameId,
      this.time,
    );
    const orientation = this.system.frames.transformOrientation(
      this.ship.body.orientation,
      current.inertialFrameId,
      body.inertialFrameId,
      this.time,
    );

    // Arrive above the point of the target nearest the approach direction, so a
    // jump does not always deposit the vessel over the same longitude.
    const radius = body.radius * (1 + altitudeFraction);
    const approach =
      converted.position.lengthSq() > 1 ? converted.position.normalized() : new Vec3(0, 0, 1);
    const position = approach.clone().mulScalar(radius);

    // Circular orbit: speed sqrt(mu / r), in a plane containing the approach
    // direction and the body's spin axis, so the orbit is roughly polar and
    // passes over interesting terrain rather than hugging the equator.
    const speed = Math.sqrt(body.mu / radius);
    let along = Vec3.crossVectors(body.spinAxis, approach);
    if (along.lengthSq() < 1e-9) along = Vec3.anyPerpendicular(approach);
    const velocity = along.normalize().mulScalar(speed);

    this.floatingOrigin.setActiveFrame(body.inertialFrameId, position, this.time);
    this.ship.rebaseTo(body, position, velocity, orientation, new Vec3());
    this.setActiveBody(body);
    this.visitedBodies.add(body.id);

    if (this.freeFlight.active) {
      this.freeFlight.engage(position, orientation);
      this.freeFlight.jumpTo(new Vec3(), body.radius, 1.6);
      // Without this, `viewBody` (whichever `freeFlightBody` last was)
      // disagrees with wherever the camera actually just jumped to, so
      // `updateActiveBodySet` keeps promoting/grouping the body free flight
      // left rather than the one now on screen — `surveyViewOf` hits this on
      // every call, since it enables free flight and only then jumps here.
      this.freeFlightBody = body;
    }
    this.onFoot = false;
    this.notify(
      'warning',
      'Debug jump',
      `Vessel placed in a ${((radius - body.radius) / 1000).toFixed(0)} km circular orbit of ${body.name}.`,
    );
    return true;
  }

  /**
   * Debug: parks the free camera where a body is three-quarters lit.
   *
   * Used by the planet survey. Framing the shot from the simulation rather than
   * from the script keeps the vector work in one place, and picking the sunward
   * side deliberately means the survey answers "is illumination right" instead
   * of "did we happen to be looking at the night side".
   */
  surveyViewOf(bodyId: string, standoffRadii = 4.2): boolean {
    const body = this.system.find(bodyId);
    if (!body) return false;
    if (!this.freeFlight.active) this.toggleFreeFlight();
    this.fastTravelTo(bodyId);

    const bodyRoot = this.system.positionOf(body, this.time);
    const starRoot = this.system.positionOf(this.system.star, this.time);
    const toStar = Vec3.subVectors(starRoot, bodyRoot).normalize();
    let side = Vec3.crossVectors(toStar, body.spinAxis);
    if (side.lengthSq() < 1e-9) side = Vec3.anyPerpendicular(toStar);
    side.normalize();

    const offset = toStar.clone().mulScalar(0.75).addScaled(side, 0.66).normalize();
    this.freeFlight.position.copy(offset).mulScalar(body.radius * standoffRadii);
    const forward = this.freeFlight.position.clone().negate().normalize();
    this.freeFlight.orientation.copy(Quat.lookRotation(forward, body.spinAxis));
    return true;
  }

  /**
   * Debug: sets the vessel down at a body's sub-solar point.
   *
   * Midday at the equator of the sunward face, so a surface capture always
   * exercises lit terrain under a lit sky rather than landing in the dark.
   */
  /**
   * Puts the vessel on the surface at a chosen local solar time.
   *
   * `solarAngleDeg` offsets the placement longitude away from the sub-solar
   * meridian, so 0 is local noon, 90 the terminator and 180 local midnight.
   * Offsetting in longitude is the right axis because the sun's apparent daily
   * motion is in longitude; latitude stays at the sub-solar parallel so the
   * comparison between two angles differs only in illumination.
   *
   * The night and twilight angles are not a test-harness luxury. Night-side
   * appearance has been repeatedly wrong here — the emissive patch, and an
   * aerial-perspective floor that was scattering more light at midnight than a
   * daylit surface reflects — and every one of those was invisible from the
   * only viewpoint the captures used, which was noon.
   */
  placeShipAtSubsolarSurface(bodyId: string, solarAngleDeg = 0): boolean {
    const body = this.system.find(bodyId);
    if (!body || !body.def.terrain) return false;

    // Sub-solar direction in the body's own rotating frame.
    const sunInertial = this.sunDirectionAt(body, new Vec3(), this.time);
    const sunFixed = this.system.frames.transformDirection(
      sunInertial,
      body.inertialFrameId,
      body.fixedFrameId,
      this.time,
    );
    const subsolar = latLonFromPosition(sunFixed);
    const latitude = subsolar.latitude;
    // latLonFromPosition works in radians; the parameter is degrees because
    // "180" is a far clearer way to ask for local midnight than "Math.PI".
    const longitude = subsolar.longitude + (solarAngleDeg * Math.PI) / 180;

    this.freeFlight.disengage();
    this.onFoot = false;
    if (this.ship.referenceBody.id !== body.id) this.fastTravelTo(bodyId);
    this.setActiveBody(body);
    this.placeShipAtSurface(body, latitude, longitude);
    return true;
  }

  /**
   * Points the free camera at the star, from wherever it currently is.
   *
   * Verification support. The glare could not be checked at all without it: a
   * landed vessel faces whatever heading `findLevelSite` gave it, so captures
   * meant to show the star came back looking at a hillside with the sun behind
   * the camera. Aiming is the only way to get the thing under test into frame.
   */
  aimCameraAtStar(): boolean {
    const body = this.ship.referenceBody;
    const cameraPosition = this.cameraPositionInFrame();
    const toStar = this.sunDirectionAt(body, cameraPosition, this.time);
    if (!this.freeFlight.active) this.toggleFreeFlight();
    // Local radial as the up reference, so the horizon stays level in frame.
    const up = cameraPosition.normalized();
    this.freeFlight.orientation.copy(Quat.lookRotation(toStar, up));
    return true;
  }

  /**
   * Per-layer visibility switches for debugging. All on by default.
   *
   * Applied every frame at the end of `render`, deliberately after the systems
   * that own each object have had their say — several of these are written by
   * their owner every frame (the glare sets its own visibility from occlusion,
   * streaming sets chunk visibility), so a one-shot toggle would be silently
   * undone within a frame.
   */
  readonly debugLayers: Record<DebugLayer, boolean> = {
    surface: true,
    atmosphere: true,
    sky: true,
    clouds: true,
    ocean: true,
    vegetation: true,
    stars: true,
    galaxy: true,
    sunGlare: true,
    distantBodies: true,
    vessel: true,
    trajectory: true,
    targetMarker: true,
  };

  private applyDebugLayers(): void {
    const d = this.debugLayers;
    this.activeTerrain?.setLayerVisible('surface', d.surface);
    this.activeTerrain?.setLayerVisible('atmosphere', d.atmosphere);
    this.activeTerrain?.setLayerVisible('sky', d.sky);
    if (this.activeClouds) this.activeClouds.root.visible = d.clouds;
    if (this.activeOcean) this.activeOcean.root.visible = d.ocean;
    if (this.activeVegetation) this.activeVegetation.root.visible = d.vegetation && this.vegetationEnabled;
    this.starfield.object.visible = d.stars;
    this.renderer.setGalaxyVisible(d.galaxy);
    if (!d.sunGlare) this.sunGlare.root.visible = false;
    this.astronomical.root.visible = d.distantBodies;
    if (!d.vessel) {
      this.shipModel.root.visible = false;
      this.characterModel.root.visible = false;
    }
    if (!d.trajectory) {
      this.trajectoryLine.line.visible = false;
      this.apsisMarkers.points.visible = false;
      this.hideRouteOverlay();
    }
    if (!d.targetMarker) this.targetMarker.points.visible = false;
  }

  /** Bodies the free camera can jump to. */
  travelTargets(): { id: string; name: string }[] {
    return this.system.bodies
      .filter((b) => b.def.kind !== 'star')
      .map((b) => ({ id: b.id, name: b.name }));
  }

  // =========================================================================
  // Tour mode
  // =========================================================================
  //
  // A scripted flight through the system: transit to a body, orbit it, spiral
  // down to a low pass over the surface, climb back out, then transit to the
  // next one — every planet in turn, each immediately followed by its own
  // moons. Entirely a camera exercise: it drives `freeFlight` exactly the way
  // `surveyViewOf` does, and never touches the vessel.
  //
  // The state machine itself is TourDirector, in src/tour/. It owns
  // `freeFlightBody`/`freeFlightFrame` outright while running (see the guards
  // in `checkViewBody` and `updateFreeFlightFrame`), because it always knows
  // which body and frame the current phase is expressed in and the
  // altitude-hysteresis logic built for manual free flight would otherwise
  // fight it. Everything below is the public surface the UI and the console
  // helpers call.

  get tourActive(): boolean {
    return this.tour.active;
  }

  /** Starts a tour. Config is optional; unset fields fall back to `tour.config.json`. */
  startTour(overrides?: Partial<TourConfig>): boolean {
    return this.tour.start(overrides);
  }

  /** Ends the tour and leaves the free camera exactly where it was. */
  stopTour(): void {
    this.tour.stop();
  }

  /** Jumps to the next or previous stop, interrupting whatever phase is running. */
  skipToStop(direction: 1 | -1): boolean {
    return this.tour.skipToStop(direction);
  }

  // =========================================================================
  // Persistence
  // =========================================================================

  /**
   * Opens the store and restores the autosave if there is one for this seed.
   *
   * Deliberately not awaited by the constructor. Storage is slow, can be
   * disabled by the browser, and can fail at any point; none of that should
   * stop the player from flying. If it works, the world is restored a moment
   * after it appears. If it does not, they get a fresh game and a notification.
   */
  async openStorage(): Promise<void> {
    try {
      const repo = await createRepository();
      this.saves = repo.saves;
      this.worldStore = repo.world;
      if (!repo.persistent) {
        this.notify('warning', 'Storage unavailable', 'Progress will be lost when this tab closes.');
      }
      await this.loadGame(AUTOSAVE_ID);
    } catch (err) {
      this.notify('warning', 'Storage unavailable', err instanceof Error ? err.message : String(err));
    }
    // Independent of the save above: a separate, best-effort restore of a
    // free-flight vantage point, if one was left engaged. Deliberately after
    // the real save so this lands on top of wherever the vessel actually is,
    // not the other way around.
    this.restoreDevSessionFreeFlight();
  }

  /**
   * Restores a free-flight camera pose from localStorage, if one was saved.
   * See DevSessionState.ts's file comment for why this exists as a separate,
   * non-authoritative record rather than part of the real save.
   */
  private restoreDevSessionFreeFlight(): void {
    const saved = loadDevSessionState().freeFlight;
    if (!saved) return;
    const body = this.system.find(saved.bodyId);
    if (!body) return;

    this.setActiveBody(body);
    this.freeFlight.position.set(saved.position[0], saved.position[1], saved.position[2]);
    this.freeFlight.orientation.set(
      saved.orientation[0],
      saved.orientation[1],
      saved.orientation[2],
      saved.orientation[3],
    );
    this.freeFlight.active = true;
    this.freeFlight.speedExponent = saved.speedExponent;
    this.freeFlightBody = body;
    this.freeFlightFrame = saved.frame;
    this.notify('info', 'Free flight restored', `Camera back where you left it near ${body.def.name}.`);
  }

  /**
   * Autosaves the free-flight pose after the camera comes to rest, so a crash
   * or a forgotten reload doesn't lose more than the last second of movement.
   * The explicit toggle-on/toggle-off saves and the `beforeunload` handler
   * remain the primary path; this only covers the gap while free flight stays
   * engaged for a while. Debounced, not saved every idle frame, so parking the
   * camera to look at something doesn't write to localStorage every tick.
   */
  private updateFreeFlightAutosave(rotation: Vec3, translation: Vec3, wheelNotches: number, dt: number): void {
    const moving = rotation.lengthSq() > 0 || translation.lengthSq() > 0 || wheelNotches !== 0;
    if (moving) {
      this.freeFlightAutosavePending = true;
      this.freeFlightIdleFor = 0;
      return;
    }
    if (!this.freeFlightAutosavePending) return;
    this.freeFlightIdleFor += dt;
    if (this.freeFlightIdleFor < Game.FREE_FLIGHT_AUTOSAVE_DEBOUNCE) return;
    // Resets freeFlightAutosavePending/freeFlightIdleFor on write.
    this.persistDevSessionFreeFlight();
  }

  /** Mirrors the live free-flight pose (or its absence) into DevSessionState. */
  private persistDevSessionFreeFlight(): void {
    const freeFlight: DevSessionFreeFlight | null =
      this.freeFlight.active && this.freeFlightBody
        ? {
            bodyId: this.freeFlightBody.id,
            frame: this.freeFlightFrame,
            position: [this.freeFlight.position.x, this.freeFlight.position.y, this.freeFlight.position.z],
            orientation: [
              this.freeFlight.orientation.x,
              this.freeFlight.orientation.y,
              this.freeFlight.orientation.z,
              this.freeFlight.orientation.w,
            ],
            speedExponent: this.freeFlight.speedExponent,
          }
        : null;
    patchDevSessionState({ freeFlight });
    this.freeFlightAutosavePending = false;
    this.freeFlightIdleFor = 0;
  }

  private saveContext(): SaveContext {
    return {
      seed: this.system.seed,
      time: this.time,
      ship: this.ship,
      visitedBodies: this.visitedBodies,
      onFoot: this.onFoot,
      characterPositionFixed: this.onFoot ? this.character.state.position.clone() : null,
      bodyId: this.ship.referenceBody.id,
    };
  }

  /** Writes the current state. Never throws; reports through notifications. */
  async saveGame(id = AUTOSAVE_ID, name = 'Autosave'): Promise<boolean> {
    if (!this.saves || this.saveInFlight) return false;
    this.saveInFlight = true;
    try {
      await this.saves.save(buildSave(this.saveContext(), id, name));
      this.notify('info', 'Saved', `${name} written at T+${Math.round(this.time)} s.`);
      return true;
    } catch (err) {
      this.notify('warning', 'Save failed', err instanceof Error ? err.message : String(err));
      return false;
    } finally {
      this.saveInFlight = false;
    }
  }

  /** Restores a save. Returns false when there is nothing to restore. */
  async loadGame(id = AUTOSAVE_ID): Promise<boolean> {
    if (!this.saves) return false;
    const save = await this.saves.load(id);
    if (!save) return false;

    const result = applySave(save, this.saveContext(), (record) => this.restoreShip(record));
    if (!result.ok) {
      this.notify('warning', 'Save not loaded', result.reason ?? 'Incompatible save.');
      return false;
    }

    this.time = save.metadata.simulationTime;
    this.onFoot = save.player.location !== 'ship';
    if (this.onFoot && save.player.surfacePosition) {
      this.character.spawnAt(Vec3.fromArray(save.player.surfacePosition).normalize());
      this.cameraMode = 'onFoot';
    }
    this.notify('info', 'Loaded', `Resumed at T+${Math.round(this.time)} s.`);
    return true;
  }

  /**
   * Puts a saved vessel back into the simulation.
   *
   * The record stores state in a named body's inertial frame rather than in
   * absolute coordinates, because absolute coordinates would only be meaningful
   * alongside the exact epoch, and reconstructing them would reintroduce the
   * precision problem the frame hierarchy exists to avoid.
   */
  private restoreShip(record: ShipRecord): boolean {
    if (record.defId !== this.ship.def.id) return false;
    const body = this.system.find(record.frameBodyId);
    if (!body) return false;

    this.ship.rebaseTo(
      body,
      Vec3.fromArray(record.position),
      Vec3.fromArray(record.velocity),
      new Quat(
        record.orientation[0],
        record.orientation[1],
        record.orientation[2],
        record.orientation[3],
      ),
      Vec3.fromArray(record.angularVelocity),
    );
    // Without this, a save restored at any body other than the fresh-game
    // starting world left `activeSurface`/`activeTerrain` pointed at the
    // start world while the ship's own reference body — and everything
    // `render` derives from `viewBody` — pointed at the restored one. The
    // camera coordinates computed for the restored body were then fed into
    // the start world's `TerrainRenderer`, whose chunks are built from
    // *its own* small local offsets: every chunk got a wildly wrong
    // `uChunkOrigin`, which reads as a screen full of shredded triangles,
    // not a hole. `setActiveBody` is the same call every other reference-body
    // change already makes (`checkReferenceBody`, `fastTravelTo`, ...); this
    // path was just missing it.
    this.setActiveBody(body);
    for (const [tankId, amount] of record.tanks) {
      if (this.ship.tanks.has(tankId)) this.ship.tanks.set(tankId, amount);
    }
    this.ship.damage.hull = record.hullDamage;
    this.ship.damage.landingGear = record.gearDamage;
    this.ship.damage.wings = record.wingDamage;
    this.ship.power.charge = record.batteryCharge;
    this.ship.thermal.temperature = record.hullTemperature;
    if (this.ship.fieldDrive) {
      this.ship.fieldDrive.temperature = record.fieldDriveTemperature;
      this.ship.fieldDrive.damage = record.fieldDriveDamage;
    }
    return true;
  }

  /** Terrain the player has altered, keyed by chunk. */
  async persistChunkDelta(planetId: string, chunkId: string, delta: ChunkDelta): Promise<void> {
    if (!this.worldStore) return;
    try {
      await this.worldStore.saveChunkDelta(planetId, chunkId, delta);
    } catch (err) {
      console.warn('[Game] failed to persist chunk delta', err);
    }
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
    this.audio.dispose();
    this.input.dispose();
    this.pool.dispose();
    for (const s of this.surfaces.values()) s.dispose();
    for (const r of this.terrainRenderers.values()) r.dispose();
    for (const o of this.oceans.values()) o.dispose();
    for (const c of this.clouds.values()) c.dispose();
    for (const v of this.vegetation.values()) v.dispose();
    this.shipModel.dispose();
    this.characterModel.dispose();
    this.trajectoryLine.dispose();
    this.apsisMarkers.dispose();
    this.routeOverlay.dispose();
    this.targetMarker.dispose();
    this.targetArrow.dispose();
    this.sunGlare.dispose();
    this.starfield.dispose();
    this.astronomical.dispose();
    this.renderer.dispose();
  }
}
